import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance, { API_BASE_URL } from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { formatMoney, parseMoney } from '../utils/formatters';
import { createResizedPair } from '../utils/imageResize';
import TrDateInput from '../components/TrDateInput';
import ProjeGorunumleri from '../components/ProjeGorunumleri';
import ProjeSayfaYonetim from '../components/ProjeSayfa/ProjeSayfaYonetim';
import ProjeFiyatlandirmaModal from '../components/ProjeFiyatlandirma/ProjeFiyatlandirmaModal';
import ProjeSozlesmeOnayModal from '../components/ProjeSozlesmeOnay/ProjeSozlesmeOnayModal';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';

interface Proje {
    projeID: number;
    projeAdi: string;
    ada: string | null;
    parsel: string | null;
    eklenmeTarihi: string;
    baslamaTarihi: string | null;
    bitisTarihi: string | null;
    projeLogo: string | null;
    takipNo: string;
    adres: string | null;
    sehir: string | null;
    kdv: number;
    projeDurumu: string;
    aktifMi: boolean;
    imalatNotlariAktif?: boolean;
    // Projeye özel firma bilgileri (Kurumsal sayfasındakinden farklı olabilir)
    firmaBilgileriAktif?: boolean;
    firmaAdi?: string | null;
    firmaAdres?: string | null;
    firmaTelefon?: string | null;
    firmaVergiDairesi?: string | null;
    firmaVergiNo?: string | null;
}

interface ImalatKategori {
    imalatKatID: number;
    projeID: number;
    kategoriAdi: string;
    alanTipi: 'SerbestMetin' | 'TekSecim' | 'CokSecim' | 'Sayi' | 'Tarih';
    secenekler?: string;
    aciklama?: string;
    siraNo: number;
    aktifMi: boolean;
}

interface Dosya {
    dosyaID: number;
    takipNo: string;
    eklenmeTarihi: string;
    aciklama: string;
    dosyaAdi: string;
    dosyaTuru: number;
    aktifMi: boolean;
}

interface Blok {
    blokID: number;
    blokAdi: string;
    takipNo: string;
    aktifMi: boolean;
    projeID: number;
}

interface Daire {
    daireID: number;
    daireNo: string;
    takipNo: string;
    sozlesmeTakipNo?: string;
    projeID: number;
    blokID: number;
    katID: number;
    cepheID: number;
    manzaraID: number;
    daireTipID: number;
    daireDurumu: string;
    yataySira: number;
    listeFiyati?: number;
    netM2?: number;
    brutM2?: number;
    paraBirimiID?: number;
    aciklama?: string;
    aktifMi: boolean;
}

interface DaireOzellik {
    daireOzellikId?: number;
    daireTakipNo: string;
    ozellik: string;
    deger: string;
    aktif: boolean;
}

interface ProjeOzellik {
    projeOzellikId?: number;
    projeTakipNo: string;
    ozellik: string;
    deger: string;
    aktif: boolean;
}

interface SunumBelge {
    sunumBelgeId: number;
    takipNo: string;
    aciklama?: string;
    sunumBelgeTuru: string;
    sunumBelgeAdi?: string;
    aktif: boolean;
    eklenmeTarihi: string;
    sira?: number;
}

interface DaireFiyatGecmis {
    fiyatGecmisID: number;
    daireID: number;
    daireTakipNo: string;
    eskiFiyat: number | null;
    yeniFiyat: number | null;
    degisimTarihi: string;
    kullaniciID: number;
    kullaniciAdi: string;
}

const durumuMeta: Record<string, {bg:string, fg:string, dot:string}> = {
    'Satışta': { bg: '#dcfce7', fg: '#15803d', dot: '#22c55e' },
    'Rezerve': { bg: '#fef3c7', fg: '#b45309', dot: '#f59e0b' },
    'Sözleşme': { bg: '#dbeafe', fg: '#1d4ed8', dot: '#3b82f6' },
};

const projeDurumuMeta: Record<string, {bg:string, fg:string}> = {
    'Devam Ediyor': { bg: '#fef3c7', fg: '#b45309' },
    'Tamamlandı': { bg: '#dcfce7', fg: '#15803d' },
    'Gelecek': { bg: '#e0f2fe', fg: '#0369a1' },
};

const ProjeDetay: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [proje, setProje] = useState<Proje | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showSablonModal, setShowSablonModal] = useState(false);
    const [showProjeSayfaModal, setShowProjeSayfaModal] = useState(false);
    const [showFiyatlandirmaModal, setShowFiyatlandirmaModal] = useState(false);
    const [showSozlesmeOnayModal, setShowSozlesmeOnayModal] = useState(false);
    const [aktifKullanicilar, setAktifKullanicilar] = useState<{ kullaniciID: number; adSoyad: string }[]>([]);
    const [projeYoneticiIDler, setProjeYoneticiIDler] = useState<number[]>([]);
    const [showDosyaModal, setShowDosyaModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showDaireEditModal, setShowDaireEditModal] = useState(false);
    const [selectedDaire, setSelectedDaire] = useState<Daire | null>(null);
    const [daireler, setDaireler] = useState<Daire[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const pageSize = 10;
    const [pdfData, setPdfData] = useState<string | null>(null);
    const [showBlokModal, setShowBlokModal] = useState(false);
    const [showBlokEditModal, setShowBlokEditModal] = useState(false);
    const [selectedSablon, setSelectedSablon] = useState<Dosya | null>(null);
    const [selectedBlok, setSelectedBlok] = useState<Blok | null>(null);
    const [bloklar, setBloklar] = useState<Blok[]>([]);
    const [blokAdi, setBlokAdi] = useState('');
    const [selectedDosya, setSelectedDosya] = useState<Dosya | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [sablonFile, setSablonFile] = useState<File | null>(null);
    const [daireTipleri, setDaireTipleri] = useState<Array<{daireTipID: number, tip: string}>>([]);
    const [katlar, setKatlar] = useState<Array<{katID: number, katAdi: string}>>([]);
    const [cepheler, setCepheler] = useState<Array<{cepheID: number, cepheAdi: string}>>([]);
    const [manzaralar, setManzaralar] = useState<Array<{manzaraID: number, manzaraAdi: string}>>([]);
    const [paraBirimleri, setParaBirimleri] = useState<Array<{paraBirimiID: number, birim: string}>>([]);
    const [showDaireModal, setShowDaireModal] = useState(false);
    const [daire, setDaire] = useState<Partial<Daire>>({});
    const [dosyaFile, setDosyaFile] = useState<File | null>(null);
    const [sablonAciklama, setSablonAciklama] = useState('');
    const [dosyaAciklama, setDosyaAciklama] = useState('');
    const [formData, setFormData] = useState<Proje | null>(null);
    const [sablonlar, setSablonlar] = useState<Dosya[]>([]);
    const [dosyalar, setDosyalar] = useState<Dosya[]>([]);
    const [ddOpen, setDdOpen] = useState(false);
    const [hoveredRow, setHoveredRow] = useState<number | null>(null);
    const ddRef = useRef<HTMLDivElement>(null);
    const [daireOzellikler, setDaireOzellikler] = useState<DaireOzellik[]>([]);
    const [ozellikOptions, setOzellikOptions] = useState<string[]>([]);
    const [degerOptionsMap, setDegerOptionsMap] = useState<Record<string, string[]>>({});
    const [showProjeOzellikModal, setShowProjeOzellikModal] = useState(false);
    const [projeOzellikler, setProjeOzellikler] = useState<ProjeOzellik[]>([]);
    const [projeOzellikSaving, setProjeOzellikSaving] = useState(false);
    const [projeOzellikOptions, setProjeOzellikOptions] = useState<string[]>([]);
    const [projeDegerOptionsMap, setProjeDegerOptionsMap] = useState<Record<string, string[]>>({});
    const [showSunumBelgeModal, setShowSunumBelgeModal] = useState(false);
    const [sunumBelgeler, setSunumBelgeler] = useState<SunumBelge[]>([]);
    const [sunumBelgeSaving, setSunumBelgeSaving] = useState(false);
    const [suruklenenGrupIdx, setSuruklenenGrupIdx] = useState<number | null>(null);
    const [uzerindeGrupIdx, setUzerindeGrupIdx] = useState<number | null>(null);
    const [sunumYeniForm, setSunumYeniForm] = useState({ aciklama: '', tur: 'Görsel', videoLink: '' });
    const [sunumFile, setSunumFile] = useState<File | null>(null);
    const [showSunumEkleForm, setShowSunumEkleForm] = useState(false);
    const [showSunumPreview, setShowSunumPreview] = useState<SunumBelge | null>(null);
    const [daireSunumBelgeler, setDaireSunumBelgeler] = useState<SunumBelge[]>([]);
    const [sunumDaireFilter, setSunumDaireFilter] = useState({ blokID: '', daireTipID: '', katID: '', cepheID: '', daireAdi: '' });
    const [sunumSeciliTakipNolar, setSunumSeciliTakipNolar] = useState<string[]>([]);
    const [editBelgeAdi, setEditBelgeAdi] = useState<string | null>(null);
    const [editGrupTakipNolar, setEditGrupTakipNolar] = useState<string[]>([]);
    const [editGrupFilter, setEditGrupFilter] = useState({ blokID: '', daireTipID: '', katID: '', cepheID: '', daireAdi: '' });
    const [editGrupSaving, setEditGrupSaving] = useState(false);
    const [daireSunumSaving, setDaireSunumSaving] = useState(false);
    const [daireSunumForm, setDaireSunumForm] = useState({ aciklama: '', tur: 'Görsel', videoLink: '' });
    const [daireSunumFile, setDaireSunumFile] = useState<File | null>(null);
    const [showDaireSunumEkleForm, setShowDaireSunumEkleForm] = useState(false);
    const [fiyatGecmisi, setFiyatGecmisi] = useState<DaireFiyatGecmis[]>([]);

    // Toplu Fiyat Güncelleme
    const [showTopluFiyatModal, setShowTopluFiyatModal] = useState(false);
    const [topluFiyatDaireler, setTopluFiyatDaireler] = useState<(Daire & { yeniFiyat: number | null })[]>([]);
    const [topluSecili, setTopluSecili] = useState<Set<number>>(new Set());
    const [topluIslemTipi, setTopluIslemTipi] = useState<'yuzde' | 'sabit'>('yuzde');
    const [topluIslemYon, setTopluIslemYon] = useState<'artir' | 'azalt'>('artir');
    const [topluIslemDeger, setTopluIslemDeger] = useState<string>('');
    const [topluSaving, setTopluSaving] = useState(false);
    const [topluFilterBlok, setTopluFilterBlok] = useState<number | ''>('');
    const [topluFilterKat, setTopluFilterKat] = useState<number | ''>('');
    const [topluFilterCephe, setTopluFilterCephe] = useState<number | ''>('');
    const [topluFilterDaireTip, setTopluFilterDaireTip] = useState<number | ''>('');
    const [topluFilterDaireNo, setTopluFilterDaireNo] = useState('');

    const [showGorunumModal, setShowGorunumModal] = useState(false);
    const [showImalatKatModal, setShowImalatKatModal] = useState(false);
    const [imalatKategoriler, setImalatKategoriler] = useState<ImalatKategori[]>([]);
    const [imalatKatLoading, setImalatKatLoading] = useState(false);
    const [imalatKatForm, setImalatKatForm] = useState<{ kategoriAdi: string; alanTipi: ImalatKategori['alanTipi']; secenekler: string[]; aciklama: string } | null>(null);
    const [imalatKatEditID, setImalatKatEditID] = useState<number | null>(null);
    const [imalatSecenekInput, setImalatSecenekInput] = useState('');
    const [imalatAktifToggling, setImalatAktifToggling] = useState(false);
    const [showImalatRaporModal, setShowImalatRaporModal] = useState(false);
    const [imalatRaporData, setImalatRaporData] = useState<any[]>([]);
    const [imalatRaporLoading, setImalatRaporLoading] = useState(false);
    const [raporFilterKatID, setRaporFilterKatID] = useState<number | ''>('');
    const [raporFilterBlokID, setRaporFilterBlokID] = useState<number | ''>('');

    const [filterBlokID, setFilterBlokID] = useState<number | ''>('');
    const [filterKatID, setFilterKatID] = useState<number | ''>('');
    const [filterCepheID, setFilterCepheID] = useState<number | ''>('');
    const [filterDaireTipID, setFilterDaireTipID] = useState<number | ''>('');
    const [filterManzaraID, setFilterManzaraID] = useState<number | ''>('');
    const [filterDaireNo, setFilterDaireNo] = useState<string>('');
    const [filterMinFiyat, setFilterMinFiyat] = useState<string>('');
    const [filterMaxFiyat, setFilterMaxFiyat] = useState<string>('');
    const [filterDurum, setFilterDurum] = useState<string>('');

    // Excel toplu daire ekleme
    interface ExcelDaireRow {
        daireNo: string;
        blokAdi: string;
        katAdi: string;
        daireTipi: string;
        cephe: string;
        manzara: string;
        netM2: string;
        brutM2: string;
        listeFiyati: string;
        aciklama: string;
        // Resolved IDs
        blokID?: number;
        katID?: number;
        daireTipID?: number;
        cepheID?: number;
        manzaraID?: number;
        hatalar: string[];
    }
    const [showExcelModal, setShowExcelModal] = useState(false);
    const [excelRows, setExcelRows] = useState<ExcelDaireRow[]>([]);
    const [excelSaving, setExcelSaving] = useState(false);
    const excelFileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ddRef.current && !ddRef.current.contains(e.target as Node)) setDdOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const fetchDaireTipleri = async () => {
        try {
            const response = await axiosInstance.get('/api/DaireTipleri');
            setDaireTipleri(response.data);
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const fetchKatlar = async () => {
        try {
            const response = await axiosInstance.get('/api/Katlar');
            setKatlar(response.data);
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const fetchCepheler = async () => {
        try {
            const response = await axiosInstance.get('/api/Cepheler');
            setCepheler(response.data);
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const fetchManzaralar = async () => {
        try {
            const response = await axiosInstance.get('/api/Manzaralar');
            setManzaralar(response.data);
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const fetchParaBirimleri = async () => {
        try {
            const response = await axiosInstance.get('/api/ParaBirimleri');
            setParaBirimleri(response.data);
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const fetchOzellikOptions = async () => {
        try {
            const res = await axiosInstance.get('/api/DaireOzellikler/ozellikler');
            setOzellikOptions(res.data);
        } catch {}
    };

    const fetchFiyatGecmisi = async (daireID: number) => {
        try {
            const res = await axiosInstance.get(`/api/Daireler/${daireID}/fiyat-gecmisi`);
            setFiyatGecmisi(res.data);
        } catch {
            setFiyatGecmisi([]);
        }
    };

    /** Toplu fiyat modalinde filtrelerden GEÇEN daireler.
     *  Hem tablo hem de "Uygula" bunu kullanır: filtre yalnızca satırları gizlerse
     *  güncelleme filtre dışındaki dairelere de uygulanıyordu. */
    const topluGorunenler = useMemo(() => topluFiyatDaireler.filter(d => {
        if (topluFilterBlok !== '' && d.blokID !== topluFilterBlok) return false;
        if (topluFilterKat !== '' && d.katID !== topluFilterKat) return false;
        if (topluFilterCephe !== '' && d.cepheID !== topluFilterCephe) return false;
        if (topluFilterDaireTip !== '' && d.daireTipID !== topluFilterDaireTip) return false;
        if (topluFilterDaireNo && !d.daireNo.toLowerCase().includes(topluFilterDaireNo.toLowerCase())) return false;
        return true;
    }), [topluFiyatDaireler, topluFilterBlok, topluFilterKat, topluFilterCephe, topluFilterDaireTip, topluFilterDaireNo]);

    const openTopluFiyatModal = () => {
        const liste = daireler
            .filter(d => d.aktifMi)
            .map(d => ({ ...d, yeniFiyat: d.listeFiyati ?? null }));
        setTopluFiyatDaireler(liste);
        setTopluSecili(new Set(liste.map(d => d.daireID)));
        setTopluIslemTipi('yuzde');
        setTopluIslemYon('artir');
        setTopluIslemDeger('');
        setTopluFilterBlok('');
        setTopluFilterKat('');
        setTopluFilterCephe('');
        setTopluFilterDaireTip('');
        setTopluFilterDaireNo('');
        setShowTopluFiyatModal(true);
    };

    const topluUygula = () => {
        const deger = parseFloat(topluIslemDeger.replace(',', '.'));
        if (isNaN(deger) || deger <= 0) { toast.error(t('msg.zorunluAlanlar')); return; }
        // Yalnızca filtreden geçen VE seçili olan daireler güncellenir.
        const hedefIDler = new Set(topluGorunenler.map(d => d.daireID));
        setTopluFiyatDaireler(prev => prev.map(d => {
            if (!hedefIDler.has(d.daireID) || !topluSecili.has(d.daireID)) return d;
            const base = d.listeFiyati ?? 0;
            let yeni: number;
            if (topluIslemTipi === 'yuzde') {
                yeni = topluIslemYon === 'artir' ? base * (1 + deger / 100) : base * (1 - deger / 100);
            } else {
                yeni = topluIslemYon === 'artir' ? base + deger : base - deger;
            }
            return { ...d, yeniFiyat: Math.round(yeni) };
        }));
    };

    const topluKaydet = async () => {
        const degisecekler = topluFiyatDaireler.filter(d =>
            topluSecili.has(d.daireID) &&
            d.yeniFiyat !== null &&
            d.yeniFiyat !== (d.listeFiyati ?? null)
        );
        if (degisecekler.length === 0) { toast.info(t('msg.bilgi')); return; }
        setTopluSaving(true);
        let basarili = 0;
        let hatali = 0;
        for (const d of degisecekler) {
            try {
                const guncellenmis = { ...d, listeFiyati: d.yeniFiyat };
                await axiosInstance.post(`/api/Daireler/update/${d.daireID}`, guncellenmis);
                basarili++;
            } catch {
                hatali++;
            }
        }
        setTopluSaving(false);
        if (basarili > 0) toast.success(t('msg.islemBasarili'));
        if (hatali > 0) toast.error(t('msg.islemHatasi'));
        setShowTopluFiyatModal(false);
        fetchDaireler(currentPage);
    };

    const fetchDegerOptions = async (ozellik: string) => {
        if (!ozellik || degerOptionsMap[ozellik]) return;
        try {
            const res = await axiosInstance.get(`/api/DaireOzellikler/degerler?ozellik=${encodeURIComponent(ozellik)}`);
            setDegerOptionsMap(prev => ({ ...prev, [ozellik]: res.data }));
        } catch {}
    };

    const fetchDaireOzellikler = async (takipNo: string) => {
        try {
            const res = await axiosInstance.get(`/api/DaireOzellikler/daire/${takipNo}`);
            setDaireOzellikler(res.data);
        } catch {
            setDaireOzellikler([]);
        }
    };

    const saveDaireOzellikler = async (takipNo: string) => {
        for (const oz of daireOzellikler) {
            if (!oz.ozellik.trim() || !oz.deger.trim()) continue;
            if (oz.daireOzellikId) {
                await axiosInstance.post(`/api/DaireOzellikler/update/${oz.daireOzellikId}`, { ...oz, daireTakipNo: takipNo });
            } else {
                await axiosInstance.post('/api/DaireOzellikler', { daireTakipNo: takipNo, ozellik: oz.ozellik, deger: oz.deger, aktif: true });
            }
        }
    };

    useEffect(() => {
        fetchDaireTipleri();
        fetchKatlar();
        fetchCepheler();
        fetchManzaralar();
        fetchParaBirimleri();
    }, []);

    useEffect(() => {
        if (showDaireModal) {
            setDaireOzellikler([]);
            fetchOzellikOptions();
        }
    }, [showDaireModal]);

    useEffect(() => {
        if (showDaireEditModal && selectedDaire?.takipNo) {
            fetchDaireOzellikler(selectedDaire.takipNo);
            fetchOzellikOptions();
            fetchDaireSunumBelgeler(selectedDaire.takipNo);
            if (selectedDaire.daireID) fetchFiyatGecmisi(selectedDaire.daireID);
        } else {
            setDaireOzellikler([]);
            setDaireSunumBelgeler([]);
            setShowDaireSunumEkleForm(false);
            setFiyatGecmisi([]);
        }
    }, [showDaireEditModal, selectedDaire?.takipNo]);

    const fetchSablonlar = async () => {
        if (proje?.takipNo) {
            try {
                const response = await axiosInstance.get(`/api/Dosyalar/takip/${proje.takipNo}`, {
                    params: { dosyaTuru: 2 }
                });
                setSablonlar(response.data);
            } catch (error) {
                toast.error(t('msg.islemHatasi'));
            }
        }
    };

    const fetchProje = async () => {
        try {
            const response = await axiosInstance.get(`/api/Projeler/${id}`);
            setProje(response.data);
            setFormData(response.data);
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
            navigate('/projeler');
        }
    };

    useEffect(() => {
        fetchProje();
    }, [id]);

    useEffect(() => {
        if (proje?.takipNo) {
            fetchProjeOzellikler();
        }
    }, [proje?.takipNo]);

    const fetchSunumBelgeler = async () => {
        if (!proje?.projeID) return;
        try {
            const res = await axiosInstance.get(`/api/SunumBelgeleri/proje/${proje.projeID}`);
            setSunumBelgeler(res.data);
        } catch {
            setSunumBelgeler([]);
        }
    };

    /** Sunum belgesi gruplarının yeni sırasını kaydeder.
     *  Belgeler ekranda SunumBelgeAdi bazında gruplandığı için sıra da grup
     *  bazında verilir; aynı dosyanın tüm daire kayıtları aynı değeri alır.
     *  Daire detayındaki galeri bu sıraya göre listelenir. */
    const handleSiraKaydet = async (siraliGruplar: { sunumBelgeAdi: string; ilkBelgeId: number }[]) => {
        try {
            await axiosInstance.post('/api/SunumBelgeleri/sirala', {
                gruplar: siraliGruplar.map((g, i) => ({
                    // "video-123" anahtarı gerçek bir dosya adı değildir; bu kayıtlar ID ile sıralanır
                    sunumBelgeAdi: g.sunumBelgeAdi.startsWith('video-') ? null : g.sunumBelgeAdi,
                    sunumBelgeId: g.sunumBelgeAdi.startsWith('video-') ? g.ilkBelgeId : null,
                    sira: i + 1,
                })),
            });
            await fetchSunumBelgeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    /** Bir grubu listede yeni konuma taşır (sıra kutusu ve sürükle-bırak ortak kullanır). */
    const grupTasi = (
        gruplar: { sunumBelgeAdi: string; ilkBelgeId: number }[],
        kaynakIdx: number,
        hedefIdx: number,
    ) => {
        if (kaynakIdx === hedefIdx || kaynakIdx < 0 || hedefIdx < 0) return;
        const yeni = [...gruplar];
        const [tasinan] = yeni.splice(kaynakIdx, 1);
        yeni.splice(Math.min(hedefIdx, yeni.length), 0, tasinan);
        handleSiraKaydet(yeni);
    };

    const handleSunumBelgeYukle = async () => {
        if (!proje?.projeID) return;
        const filtrelenenler = daireler.filter(d => {
            if (!d.aktifMi) return false;
            if (sunumDaireFilter.blokID && d.blokID.toString() !== sunumDaireFilter.blokID) return false;
            if (sunumDaireFilter.daireTipID && d.daireTipID.toString() !== sunumDaireFilter.daireTipID) return false;
            if (sunumDaireFilter.katID && d.katID.toString() !== sunumDaireFilter.katID) return false;
            if (sunumDaireFilter.cepheID && d.cepheID.toString() !== sunumDaireFilter.cepheID) return false;
            if (sunumDaireFilter.daireAdi && !d.daireNo.toLowerCase().includes(sunumDaireFilter.daireAdi.toLowerCase())) return false;
            return true;
        });
        const hedefTakipNolar = filtrelenenler.filter(d => sunumSeciliTakipNolar.includes(d.takipNo)).map(d => d.takipNo);
        if (hedefTakipNolar.length === 0) { toast.error('En az bir daire seçmelisiniz.'); return; }
        setSunumBelgeSaving(true);
        try {
            const fd = new FormData();
            hedefTakipNolar.forEach(tn => fd.append('takipNolar', tn));
            fd.append('aciklama', sunumYeniForm.aciklama);
            fd.append('sunumBelgeTuru', sunumYeniForm.tur);
            if (sunumYeniForm.tur === 'Video') {
                fd.append('videoLink', sunumYeniForm.videoLink);
            } else {
                if (!sunumFile) { toast.error(t('msg.zorunluAlanlar')); setSunumBelgeSaving(false); return; }
                if (sunumYeniForm.tur === 'Görsel') {
                    const { mainFile, thumbFile } = await createResizedPair(sunumFile);
                    fd.append('file', mainFile);
                    fd.append('thumbnailFile', thumbFile);
                } else {
                    fd.append('file', sunumFile);
                }
            }
            await axiosInstance.post('/api/SunumBelgeleri/upload-coklu', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(t('msg.islemBasarili'));
            setSunumYeniForm({ aciklama: '', tur: 'Görsel', videoLink: '' });
            setSunumFile(null);
            setSunumSeciliTakipNolar([]);
            setSunumDaireFilter({ blokID: '', daireTipID: '', katID: '', cepheID: '', daireAdi: '' });
            setShowSunumEkleForm(false);
            fetchSunumBelgeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSunumBelgeSaving(false);
        }
    };

    const handleSunumBelgeSil = async (id: number) => {
        try {
            await axiosInstance.delete(`/api/SunumBelgeleri/${id}`);
            toast.success(t('msg.islemBasarili'));
            fetchSunumBelgeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleBelgeGrubuSil = async (kayitlar: SunumBelge[]) => {
        try {
            for (const kayit of kayitlar) {
                await axiosInstance.delete(`/api/SunumBelgeleri/${kayit.sunumBelgeId}`);
            }
            toast.success(t('msg.islemBasarili'));
            setEditBelgeAdi(null);
            fetchSunumBelgeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleBelgeGrubuGuncelle = async () => {
        if (!editBelgeAdi) return;
        setEditGrupSaving(true);
        try {
            await axiosInstance.post('/api/SunumBelgeleri/guncelle-atama', {
                sunumBelgeAdi: editBelgeAdi,
                takipNolar: editGrupTakipNolar
            });
            toast.success(t('msg.islemBasarili'));
            setEditBelgeAdi(null);
            fetchSunumBelgeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setEditGrupSaving(false);
        }
    };

    const computeDaireLabel = (takipNolar: string[]): string => {
        const aktifDaireler = daireler.filter(d => d.aktifMi);
        const selected = aktifDaireler.filter(d => takipNolar.includes(d.takipNo));
        if (selected.length === 0) return 'Hiç daire yok';
        if (selected.length === aktifDaireler.length) return 'Tüm Daireler';
        const parts: string[] = [];
        const covered = new Set<string>();
        bloklar.forEach(blok => {
            const blokDaires = aktifDaireler.filter(d => d.blokID === blok.blokID);
            if (blokDaires.length > 0 && blokDaires.every(d => takipNolar.includes(d.takipNo))) {
                parts.push(blok.blokAdi);
                blokDaires.forEach(d => covered.add(d.takipNo));
            }
        });
        daireTipleri.forEach(tip => {
            const tipDaires = aktifDaireler.filter(d => d.daireTipID === tip.daireTipID && !covered.has(d.takipNo));
            if (tipDaires.length > 0 && tipDaires.every(d => takipNolar.includes(d.takipNo))) {
                parts.push(tip.tip);
                tipDaires.forEach(d => covered.add(d.takipNo));
            }
        });
        katlar.forEach(kat => {
            const katDaires = aktifDaireler.filter(d => d.katID === kat.katID && !covered.has(d.takipNo));
            if (katDaires.length > 0 && katDaires.every(d => takipNolar.includes(d.takipNo))) {
                parts.push(kat.katAdi);
                katDaires.forEach(d => covered.add(d.takipNo));
            }
        });
        selected.filter(d => !covered.has(d.takipNo)).forEach(d => parts.push(d.daireNo));
        return parts.join(', ') || `${selected.length} daire`;
    };

    const fetchDaireSunumBelgeler = async (takipNo: string) => {
        try {
            const res = await axiosInstance.get(`/api/SunumBelgeleri/takip/${takipNo}`);
            setDaireSunumBelgeler(res.data);
        } catch {
            setDaireSunumBelgeler([]);
        }
    };

    const handleDaireSunumYukle = async () => {
        if (!selectedDaire?.takipNo) return;
        setDaireSunumSaving(true);
        try {
            const fd = new FormData();
            fd.append('takipNo', selectedDaire.takipNo);
            fd.append('aciklama', daireSunumForm.aciklama);
            fd.append('sunumBelgeTuru', daireSunumForm.tur);
            if (daireSunumForm.tur === 'Video') {
                fd.append('videoLink', daireSunumForm.videoLink);
            } else {
                if (!daireSunumFile) { toast.error(t('msg.zorunluAlanlar')); setDaireSunumSaving(false); return; }
                if (daireSunumForm.tur === 'Görsel') {
                    const { mainFile, thumbFile } = await createResizedPair(daireSunumFile);
                    fd.append('file', mainFile);
                    fd.append('thumbnailFile', thumbFile);
                } else {
                    fd.append('file', daireSunumFile);
                }
            }
            await axiosInstance.post('/api/SunumBelgeleri/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(t('msg.islemBasarili'));
            setDaireSunumForm({ aciklama: '', tur: 'Görsel', videoLink: '' });
            setDaireSunumFile(null);
            setShowDaireSunumEkleForm(false);
            fetchDaireSunumBelgeler(selectedDaire.takipNo);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setDaireSunumSaving(false);
        }
    };

    const handleDaireSunumSil = async (id: number) => {
        try {
            await axiosInstance.delete(`/api/SunumBelgeleri/${id}`);
            toast.success(t('msg.islemBasarili'));
            if (selectedDaire?.takipNo) fetchDaireSunumBelgeler(selectedDaire.takipNo);
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const fetchProjeOzellikler = async () => {
        if (!proje?.takipNo) return;
        try {
            const res = await axiosInstance.get(`/api/ProjeOzellikler/proje/${proje.takipNo}`);
            setProjeOzellikler(res.data);
        } catch {
            setProjeOzellikler([]);
        }
    };

    const fetchProjeOzellikOptions = async () => {
        try {
            const res = await axiosInstance.get('/api/ProjeOzellikler/ozellikler');
            setProjeOzellikOptions(res.data);
        } catch {}
    };

    const fetchProjeDegerOptions = async (ozellik: string) => {
        if (!ozellik || projeDegerOptionsMap[ozellik]) return;
        try {
            const res = await axiosInstance.get(`/api/ProjeOzellikler/degerler?ozellik=${encodeURIComponent(ozellik)}`);
            setProjeDegerOptionsMap(prev => ({ ...prev, [ozellik]: res.data }));
        } catch {}
    };

    const handleProjeOzellikKaydet = async () => {
        if (!proje?.takipNo) return;
        setProjeOzellikSaving(true);
        try {
            // Save new ones (no id)
            for (const oz of projeOzellikler) {
                if (!oz.ozellik.trim() || !oz.deger.trim()) continue;
                if (!oz.projeOzellikId) {
                    await axiosInstance.post('/api/ProjeOzellikler', {
                        projeTakipNo: proje.takipNo,
                        ozellik: oz.ozellik,
                        deger: oz.deger,
                        aktif: true
                    });
                } else {
                    await axiosInstance.post(`/api/ProjeOzellikler/update/${oz.projeOzellikId}`, {
                        projeOzellikId: oz.projeOzellikId,
                        projeTakipNo: proje.takipNo,
                        ozellik: oz.ozellik,
                        deger: oz.deger,
                        aktif: true
                    });
                }
            }
            toast.success(t('msg.islemBasarili'));
            fetchProjeOzellikler();
            setShowProjeOzellikModal(false);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setProjeOzellikSaving(false);
        }
    };

    const fetchImalatKategoriler = async () => {
        if (!proje?.projeID) return;
        setImalatKatLoading(true);
        try {
            const res = await axiosInstance.get(`/api/ImalatKategorileri?projeID=${proje.projeID}`);
            setImalatKategoriler(res.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setImalatKatLoading(false);
        }
    };

    const handleImalatAktifToggle = async () => {
        if (!proje) return;
        setImalatAktifToggling(true);
        try {
            const yeni = !proje.imalatNotlariAktif;
            await axiosInstance.post(`/api/Projeler/${proje.projeID}/imalat-aktif`, { aktifMi: yeni });
            setProje({ ...proje, imalatNotlariAktif: yeni });
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setImalatAktifToggling(false);
        }
    };

    const handleImalatKatSave = async () => {
        if (!proje || !imalatKatForm) return;
        if (!imalatKatForm.kategoriAdi.trim()) { toast.error(t('msg.zorunluAlanlar')); return; }
        try {
            const seceneklerJson = ['TekSecim','CokSecim'].includes(imalatKatForm.alanTipi) ? JSON.stringify(imalatKatForm.secenekler) : null;
            if (imalatKatEditID) {
                await axiosInstance.post(`/api/ImalatKategorileri/${imalatKatEditID}/update`, { ...imalatKatForm, secenekler: seceneklerJson });
            } else {
                await axiosInstance.post('/api/ImalatKategorileri', { projeID: proje.projeID, ...imalatKatForm, secenekler: seceneklerJson });
            }
            toast.success(t('msg.islemBasarili'));
            setImalatKatForm(null);
            setImalatKatEditID(null);
            fetchImalatKategoriler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleImalatKatDelete = async (id: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            await axiosInstance.delete(`/api/ImalatKategorileri/${id}`);
            toast.success(t('msg.islemBasarili'));
            fetchImalatKategoriler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const fetchImalatRapor = async () => {
        if (!proje?.projeID) return;
        setImalatRaporLoading(true);
        try {
            const params: Record<string, any> = {};
            if (raporFilterKatID !== '') params.imalatKatID = raporFilterKatID;
            if (raporFilterBlokID !== '') params.blokID = raporFilterBlokID;
            const res = await axiosInstance.get(`/api/ImalatNotlari/proje/${proje.projeID}/rapor`, { params });
            setImalatRaporData(res.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setImalatRaporLoading(false);
        }
    };

    const downloadExcelTemplate = () => {
        const wb = XLSX.utils.book_new();
        const header = ['Daire No *', 'Blok Adı *', 'Kat Adı *', 'Daire Tipi *', 'Cephe *', 'Manzara *', 'Net m²', 'Brüt m²', 'Liste Fiyatı', 'Açıklama'];
        // hint rows from existing lookup data
        const blokHints = bloklar.map(b => b.blokAdi).join(', ');
        const katHints = katlar.map(k => k.katAdi).join(', ');
        const tipHints = daireTipleri.map(item => item.tip).join(', ');
        const cepheHints = cepheler.map(c => c.cepheAdi).join(', ');
        const manzaraHints = manzaralar.map(m => m.manzaraAdi).join(', ');
        const hints = [
            '',
            blokHints ? `Bloklar: ${blokHints}` : '',
            katHints ? `Katlar: ${katHints}` : '',
            tipHints ? `Tipler: ${tipHints}` : '',
            cepheHints ? `Cepheler: ${cepheHints}` : '',
            manzaraHints ? `Manzaralar: ${manzaraHints}` : '',
            '', '', '', ''
        ];
        const sample = ['101', bloklar[0]?.blokAdi || 'A Blok', katlar[0]?.katAdi || '1. Kat', daireTipleri[0]?.tip || '2+1', cepheler[0]?.cepheAdi || 'Kuzey', manzaralar[0]?.manzaraAdi || 'Bahçe', '85', '95', '1500000', ''];
        const ws = XLSX.utils.aoa_to_sheet([header, hints, sample]);
        ws['!cols'] = header.map(() => ({ wch: 18 }));
        XLSX.utils.book_append_sheet(wb, ws, 'Daireler');
        XLSX.writeFile(wb, 'daire_sablonu.xlsx');
    };

    const handleExcelUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const wb = XLSX.read(ev.target?.result, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const raw: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
                // skip header row (row 0), skip hint row (row 1 if starts with empty/hint), start from data
                const dataStart = raw.length > 1 && String(raw[1][0] || '').includes('Blok') ? 2 : 1;
                const rows: ExcelDaireRow[] = [];
                for (let i = dataStart; i < raw.length; i++) {
                    const r = raw[i];
                    if (!r || r.every((c: any) => c === '' || c === null || c === undefined)) continue;
                    const row: ExcelDaireRow = {
                        daireNo: String(r[0] ?? '').trim(),
                        blokAdi: String(r[1] ?? '').trim(),
                        katAdi: String(r[2] ?? '').trim(),
                        daireTipi: String(r[3] ?? '').trim(),
                        cephe: String(r[4] ?? '').trim(),
                        manzara: String(r[5] ?? '').trim(),
                        netM2: String(r[6] ?? '').trim(),
                        brutM2: String(r[7] ?? '').trim(),
                        listeFiyati: String(r[8] ?? '').trim(),
                        aciklama: String(r[9] ?? '').trim(),
                        hatalar: [],
                    };
                    if (!row.daireNo) row.hatalar.push('Daire No zorunlu');
                    if (!row.blokAdi) {
                        row.hatalar.push(t('msg.zorunluAlanlar'));
                    } else {
                        const blok = bloklar.find(b => b.blokAdi.toLowerCase() === row.blokAdi.toLowerCase());
                        if (blok) row.blokID = blok.blokID;
                        else row.hatalar.push(`Blok bulunamadı: "${row.blokAdi}"`);
                    }
                    if (!row.katAdi) {
                        row.hatalar.push(t('msg.zorunluAlanlar'));
                    } else {
                        const kat = katlar.find(k => k.katAdi.toLowerCase() === row.katAdi.toLowerCase());
                        if (kat) row.katID = kat.katID;
                        else row.hatalar.push(`Kat bulunamadı: "${row.katAdi}"`);
                    }
                    if (!row.daireTipi) {
                        row.hatalar.push('Daire Tipi zorunlu');
                    } else {
                        const tip = daireTipleri.find(item => item.tip.toLowerCase() === row.daireTipi.toLowerCase());
                        if (tip) row.daireTipID = tip.daireTipID;
                        else row.hatalar.push(`Daire tipi bulunamadı: "${row.daireTipi}"`);
                    }
                    if (!row.cephe) {
                        row.hatalar.push('Cephe zorunlu');
                    } else {
                        const cephe = cepheler.find(c => c.cepheAdi.toLowerCase() === row.cephe.toLowerCase());
                        if (cephe) row.cepheID = cephe.cepheID;
                        else row.hatalar.push(`Cephe bulunamadı: "${row.cephe}"`);
                    }
                    if (!row.manzara) {
                        row.hatalar.push('Manzara zorunlu');
                    } else {
                        const manzara = manzaralar.find(m => m.manzaraAdi.toLowerCase() === row.manzara.toLowerCase());
                        if (manzara) row.manzaraID = manzara.manzaraID;
                        else row.hatalar.push(`Manzara bulunamadı: "${row.manzara}"`);
                    }
                    rows.push(row);
                }
                setExcelRows(rows);
            } catch {
                toast.error(t('msg.islemHatasi'));
            }
        };
        reader.readAsBinaryString(file);
        e.target.value = '';
    };

    const saveExcelDaireler = async () => {
        if (!proje?.projeID) return;
        const gecerli = excelRows.filter(r => r.hatalar.length === 0);
        if (!gecerli.length) return;
        setExcelSaving(true);
        let basarili = 0;
        let hatali = 0;
        for (const row of gecerli) {
            try {
                await axiosInstance.post('/api/Daireler', {
                    projeID: proje.projeID,
                    daireNo: row.daireNo,
                    blokID: row.blokID,
                    katID: row.katID,
                    daireTipID: row.daireTipID,
                    cepheID: row.cepheID,
                    manzaraID: row.manzaraID,
                    netM2: row.netM2 !== '' ? parseFloat(row.netM2.replace(',', '.')) : null,
                    brutM2: row.brutM2 !== '' ? parseFloat(row.brutM2.replace(',', '.')) : null,
                    listeFiyati: row.listeFiyati !== '' ? parseFloat(row.listeFiyati.replace(/\./g, '').replace(',', '.')) : null,
                    aciklama: row.aciklama || null,
                    daireDurumu: 'Satışta',
                    aktifMi: true,
                    yataySira: 0,
                });
                basarili++;
            } catch {
                hatali++;
            }
        }
        setExcelSaving(false);
        setShowExcelModal(false);
        setExcelRows([]);
        fetchDaireler(currentPage);
        if (hatali === 0) {
            toast.success(t('msg.islemBasarili'));
        } else {
            toast.warning(`${basarili} daire eklendi, ${hatali} daire eklenemedi.`);
        }
    };

    const handleImalatRaporExcel = () => {
        if (!imalatRaporData.length) return;
        const header = ['Daire No', 'Blok', 'Kat', 'Müşteri', 'Kategori', 'Değer', 'Açıklama', 'Tarih'];
        const rows = imalatRaporData.map((r: any) => [
            r.daireNo || '',
            r.blokAdi || '',
            r.katAdi || '',
            r.musteriAdi || '',
            r.kategoriAdi || '',
            r.deger || '',
            r.aciklama || '',
            r.eklenmeTarihi ? new Date(r.eklenmeTarihi).toLocaleDateString('tr-TR') : ''
        ]);
        const csvContent = [header, ...rows].map(row => row.map((v: string) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `imalat_notlari_raporu_${proje?.projeAdi || ''}.csv`;
        link.click();
    };

    const fetchDosyalar = async () => {
        if (proje?.takipNo) {
            try {
                const response = await axiosInstance.get(`/api/Dosyalar/takip/${proje.takipNo}`, {
                    params: { dosyaTuru: 1 }
                });
                setDosyalar(response.data);
            } catch (error) {
                toast.error(t('msg.islemHatasi'));
            }
        }
    };

    const fetchBloklar = async () => {
        if (proje?.projeID) {
            try {
                const response = await axiosInstance.get(`/api/Bloklar/proje/${proje.projeID}`);
                setBloklar(response.data);
            } catch (error) {
                toast.error(t('msg.islemHatasi'));
            }
        }
    };

    const fetchDaireler = async (page: number = 1) => {
        if (!proje?.projeID) return;
        try {
            const response = await axiosInstance.get('/api/Daireler', {
                params: {
                    projeID: proje.projeID,
                    page,
                    pageSize,
                    aktifMi: true
                }
            });
            if (response.data && Array.isArray(response.data)) {
                const activeDaireler = response.data.filter((daire: Daire) => daire.aktifMi === true);
                setDaireler(activeDaireler);
                setTotalPages(Math.ceil(activeDaireler.length / pageSize));
            } else if (response.data && response.data.items && Array.isArray(response.data.items)) {
                const activeDaireler = response.data.items.filter((daire: Daire) => daire.aktifMi === true);
                setDaireler(activeDaireler);
                setTotalPages(Math.ceil(response.data.totalCount / pageSize));
            } else {
                setDaireler([]);
                setTotalPages(1);
            }
            setCurrentPage(page);
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
            setDaireler([]);
        }
    };

    useEffect(() => {
        if (proje?.takipNo) {
            fetchSablonlar();
            fetchDosyalar();
            fetchBloklar();
            fetchDaireler();
            fetchSunumBelgeler();
        }
    }, [proje?.takipNo]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData) return;

        try {
            let logoFileName = formData.projeLogo;

            if (selectedFile) {
                const formDataFile = new FormData();
                formDataFile.append('file', selectedFile);
                formDataFile.append('aciklama', 'Proje Logo');
                formDataFile.append('takipNo', formData.takipNo);

                const uploadResponse = await axiosInstance.post('/api/Dosyalar/upload', formDataFile, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                logoFileName = uploadResponse.data.dosyaAdi;
            }

            await axiosInstance.put(`/api/Projeler/${id}`, {
                ...formData,
                projeLogo: logoFileName
            });
            await axiosInstance.post(`/api/Projeler/${id}/yoneticiler`, { idListesi: projeYoneticiIDler });

            toast.success(t('msg.islemBasarili'));
            setShowModal(false);
            fetchProje();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || t('msg.islemHatasi'));
        }
    };

    const openProjeDuzenle = async () => {
        setFormData(proje);
        setShowModal(true);
        try {
            const [kullaniciRes, yoneticiRes] = await Promise.all([
                axiosInstance.get('/api/Kullanicilar/aktif-liste'),
                axiosInstance.get(`/api/Projeler/${id}/yoneticiler`),
            ]);
            setAktifKullanicilar(kullaniciRes.data || []);
            setProjeYoneticiIDler((yoneticiRes.data || []).map((y: { kullaniciID: number }) => y.kullaniciID));
        } catch {
            toast.error('Proje yöneticileri yüklenemedi.');
        }
    };

    const toggleProjeYonetici = (kullaniciID: number) =>
        setProjeYoneticiIDler(prev => prev.includes(kullaniciID) ? prev.filter(x => x !== kullaniciID) : [...prev, kullaniciID]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type === 'image/jpeg' || file.type === 'image/png') {
                setSelectedFile(file);
            } else {
                toast.error(t('msg.islemHatasi'));
                e.target.value = '';
            }
        }
    };

    const handleSablonFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.name.toLowerCase().endsWith('.docx')) {
                setSablonFile(file);
            } else {
                toast.error(t('msg.islemHatasi'));
                e.target.value = '';
            }
        }
    };

    const handleDosyaFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setDosyaFile(file);
        }
    };

    const handleDaireSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!proje?.projeID) return;

        const requiredFields = {
            daireNo: 'Daire No',
            blokID: 'Blok',
            katID: 'Kat',
            cepheID: 'Cephe',
            manzaraID: 'Manzara',
            daireTipID: 'Daire Tipi'
        };

        const missingFields = Object.entries(requiredFields)
            .filter(([field]) => !daire[field as keyof typeof daire])
            .map(([, label]) => label);

        if (missingFields.length > 0) {
            toast.error(t('msg.islemHatasi'));
            return;
        }

        try {
            const yeniDaire = {
                ...daire,
                projeID: proje.projeID,
                daireDurumu: "Satışta",
                aktifMi: true
            };

            const response = await axiosInstance.post('/api/Daireler', yeniDaire);
            const createdDaire = response.data;

            if (daireOzellikler.length > 0 && createdDaire.takipNo) {
                await saveDaireOzellikler(createdDaire.takipNo);
            }
            
            toast.success(t('msg.islemBasarili'));
            setShowDaireModal(false);
            setDaire({});
            setDaireOzellikler([]);
            
            fetchDaireler(currentPage);
        } catch (error: any) {
            if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                errorMessages.forEach((message) => {
                    if (typeof message === 'string') {
                        toast.error(message);
                    }
                });
            } else if (error.response?.data?.title) {
                toast.error(error.response.data.title);
            } else {
                toast.error(t('msg.islemHatasi'));
            }
        }
    };

    const handleDaireEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedDaire) return;

        try {
            await axiosInstance.put(`/api/Daireler/${selectedDaire.daireID}`, selectedDaire);

            if (selectedDaire.takipNo) {
                await saveDaireOzellikler(selectedDaire.takipNo);
            }

            toast.success(t('msg.islemBasarili'));
            setShowDaireEditModal(false);
            setSelectedDaire(null);
            setDaireOzellikler([]);
            fetchDaireler(currentPage);
        } catch (error: any) {
            if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                errorMessages.forEach((message) => {
                    if (typeof message === 'string') {
                        toast.error(message);
                    }
                });
            } else if (error.response?.data?.title) {
                toast.error(error.response.data.title);
            } else {
                toast.error(t('msg.islemHatasi'));
            }
        }
    };

    const handleDosyaSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!dosyaFile || !proje?.takipNo || !dosyaAciklama.trim()) return;

        try {
            const formData = new FormData();
            formData.append('file', dosyaFile);
            formData.append('takipNo', proje.takipNo);
            formData.append('aciklama', dosyaAciklama);
            formData.append('dosyaTuru', '1');

            await axiosInstance.post('/api/Dosyalar/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success(t('msg.islemBasarili'));
            setShowDosyaModal(false);
            setDosyaFile(null);
            setDosyaAciklama('');
            fetchDosyalar();
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const canPreviewFile = (fileName: string) => {
        const ext = fileName.toLowerCase().split('.').pop();
        return ['jpg', 'jpeg', 'png', 'pdf'].includes(ext || '');
    };

    const handleBlokSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!proje?.projeID || !blokAdi.trim()) return;

        try {
            await axiosInstance.post('/api/Bloklar', {
                BlokAdi: blokAdi,
                ProjeID: proje.projeID,
                AktifMi: true
            });

            toast.success(t('msg.islemBasarili'));
            setShowBlokModal(false);
            setBlokAdi('');
            fetchBloklar();
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleBlokEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedBlok || !blokAdi.trim()) return;

        try {
            await axiosInstance.put(`/api/Bloklar/${selectedBlok.blokID}`, {
                BlokAdi: blokAdi,
                AktifMi: selectedBlok.aktifMi
            });

            toast.success(t('msg.islemBasarili'));
            setShowBlokEditModal(false);
            setSelectedBlok(null);
            setBlokAdi('');
            fetchBloklar();
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleDelete = async () => {
        const itemToDelete = selectedSablon || selectedDosya || selectedBlok;
        if (!itemToDelete) return;

        try {
            if (selectedBlok) {
                await axiosInstance.delete(`/api/Bloklar/${selectedBlok.blokID}`);
                toast.success(t('msg.islemBasarili'));
                setSelectedBlok(null);
                fetchBloklar();
            } else {
                const dosya = itemToDelete as Dosya;
                await axiosInstance.delete(`/api/Dosyalar/${dosya.dosyaID}`);
                toast.success(t('msg.islemBasarili'));
                setSelectedSablon(null);
                setSelectedDosya(null);
                
                if (selectedSablon) {
                    fetchSablonlar();
                } else {
                    fetchDosyalar();
                }
            }
            setShowDeleteModal(false);
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleSablonSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!sablonFile || !proje?.takipNo || !sablonAciklama.trim()) return;

        try {
            const formData = new FormData();
            formData.append('file', sablonFile);
            formData.append('takipNo', proje.takipNo);
            formData.append('aciklama', sablonAciklama);
            formData.append('dosyaTuru', '2');

            await axiosInstance.post('/api/Dosyalar/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success(t('msg.islemBasarili'));
            setShowSablonModal(false);
            setSablonFile(null);
            setSablonAciklama('');
            fetchSablonlar();
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    /* ── computed stats ── */
    const totalValue = daireler.reduce((s, d) => s + (d.listeFiyati || 0), 0);
    const satista = daireler.filter(d => d.daireDurumu === 'Satışta').length;
    const rezerve = daireler.filter(d => d.daireDurumu === 'Rezerve').length;
    const sozlesme = daireler.filter(d => d.daireDurumu === 'Sözleşme').length;

    if (!proje) {
        return (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh'}}>
                <div style={{width:36,height:36,border:'3px solid #e2e8f0',borderTopColor:'#cd6906',borderRadius:'50%',animation:'pdSpin .7s linear infinite'}} />
                <style>{`@keyframes pdSpin{to{transform:rotate(360deg)}}`}</style>
            </div>
        );
    }

    const dm = durumuMeta;
    const pdm = projeDurumuMeta[proje.projeDurumu] || { bg: '#f1f5f9', fg: '#475569' };

    const filteredDaireler = daireler.filter(d => {
        if (filterBlokID !== '' && d.blokID !== filterBlokID) return false;
        if (filterKatID !== '' && d.katID !== filterKatID) return false;
        if (filterCepheID !== '' && d.cepheID !== filterCepheID) return false;
        if (filterDaireTipID !== '' && d.daireTipID !== filterDaireTipID) return false;
        if (filterManzaraID !== '' && d.manzaraID !== filterManzaraID) return false;
        if (filterDurum && d.daireDurumu !== filterDurum) return false;
        if (filterDaireNo && !d.daireNo.toLowerCase().includes(filterDaireNo.toLowerCase())) return false;
        const min = filterMinFiyat ? parseMoney(filterMinFiyat) : null;
        const max = filterMaxFiyat ? parseMoney(filterMaxFiyat) : null;
        if (min !== null && (d.listeFiyati === undefined || d.listeFiyati < min)) return false;
        if (max !== null && (d.listeFiyati === undefined || d.listeFiyati > max)) return false;
        return true;
    });

    const filteredTotalPages = Math.ceil(filteredDaireler.length / pageSize);
    const pagedDaireler = filteredDaireler.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const resetFilters = () => {
        setFilterBlokID('');
        setFilterKatID('');
        setFilterCepheID('');
        setFilterDaireTipID('');
        setFilterManzaraID('');
        setFilterDaireNo('');
        setFilterMinFiyat('');
        setFilterMaxFiyat('');
        setFilterDurum('');
        setCurrentPage(1);
    };

    const hasActiveFilters = filterBlokID !== '' || filterKatID !== '' || filterCepheID !== '' || filterDaireTipID !== '' || filterManzaraID !== '' || filterDaireNo !== '' || filterMinFiyat !== '' || filterMaxFiyat !== '' || filterDurum !== '';

    return (
        <>
        <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
            .pd-wrap{font-family:'Inter',sans-serif;background:#f4f6fb;min-height:100vh;padding:24px 28px}
            .pd-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:22px}
            .pd-title{font-size:22px;font-weight:700;color:#0f172a;display:flex;align-items:center;gap:12px}
            .pd-back{width:36px;height:36px;border-radius:10px;border:1px solid #e2e8f0;background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .15s}
            .pd-back:hover{border-color:#cd6906;color:#cd6906}
            .pd-actions{display:flex;gap:8px;align-items:center}
            .pd-btn{height:38px;padding:0 18px;border-radius:10px;font-size:13px;font-weight:600;border:none;cursor:pointer;display:flex;align-items:center;gap:7px;transition:all .15s}
            .pd-btn-primary{background:#cd6906;color:#fff}
            .pd-btn-primary:hover{background:#b85d05}
            .pd-btn-ghost{background:#fff;color:#475569;border:1px solid #e2e8f0}
            .pd-btn-ghost:hover{border-color:#cd6906;color:#cd6906}
            .pd-dd-wrap{position:relative}
            .pd-dd-menu{position:absolute;top:44px;right:0;background:#fff;border-radius:12px;box-shadow:0 8px 32px rgba(15,23,42,.13);padding:6px;min-width:220px;z-index:50;opacity:0;pointer-events:none;transform:translateY(-6px);transition:all .15s}
            .pd-dd-menu.pd-dd-open{opacity:1;pointer-events:auto;transform:translateY(0)}
            .pd-dd-item{display:flex;align-items:center;gap:10px;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:500;color:#334155;cursor:pointer;border:none;background:none;width:100%;text-align:left;transition:background .12s}
            .pd-dd-item:hover{background:#fff8f0;color:#cd6906}
            .pd-dd-item i{width:16px;text-align:center;font-size:13px}

            .pd-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:22px}
            .pd-stat{background:#fff;border-radius:14px;padding:18px 20px;box-shadow:0 1px 4px rgba(15,23,42,.07)}
            .pd-stat-label{font-size:11.5px;font-weight:600;color:#94a3b8;letter-spacing:.5px;margin-bottom:6px}
            .pd-stat-val{font-size:22px;font-weight:700;color:#0f172a}
            .pd-stat-sub{font-size:12px;color:#94a3b8;margin-top:4px}
            .pd-stat-dots{display:flex;gap:14px;margin-top:8px}
            .pd-stat-dot{display:flex;align-items:center;gap:5px;font-size:12px;font-weight:500}
            .pd-stat-dot::before{content:'';width:8px;height:8px;border-radius:50%}
            .pd-stat-dot.pd-sold::before{background:#3b82f6}
            .pd-stat-dot.pd-reserved::before{background:#f59e0b}
            .pd-stat-dot.pd-available::before{background:#22c55e}

            .pd-grid{display:grid;grid-template-columns:1fr 300px;gap:18px;margin-bottom:22px}
            .pd-card{background:#fff;border-radius:14px;box-shadow:0 1px 4px rgba(15,23,42,.07);overflow:hidden}
            .pd-card-head{padding:16px 20px;border-bottom:1px solid #f1f5f9;display:flex;align-items:center;justify-content:space-between}
            .pd-card-title{font-size:14px;font-weight:700;color:#0f172a}
            .pd-card-body{padding:20px}

            .pd-proje-info{display:flex;gap:20px}
            .pd-proje-logo{width:80px;height:80px;border-radius:12px;overflow:hidden;flex-shrink:0;background:#f8fafc;display:flex;align-items:center;justify-content:center}
            .pd-proje-logo img{width:100%;height:100%;object-fit:cover}
            .pd-proje-meta{flex:1}
            .pd-proje-name{font-size:18px;font-weight:700;color:#0f172a;margin-bottom:4px}
            .pd-proje-loc{font-size:13px;color:#64748b;margin-bottom:10px}
            .pd-detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px 16px}
            .pd-detail-item{font-size:12.5px;color:#64748b}
            .pd-detail-item span{color:#0f172a;font-weight:500}
            .pd-pill{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:20px;font-size:11.5px;font-weight:600}
            .pd-pill-dot{width:6px;height:6px;border-radius:50%}

            .pd-bloklar{margin-top:16px}
            .pd-bloklar-title{font-size:13px;font-weight:700;color:#0f172a;margin-bottom:10px}
            .pd-bloklar-list{display:flex;flex-wrap:wrap;gap:10px}
            .pd-blok-chip{padding:10px 22px;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;transition:all .15s;border:1px solid #e2e8f0;background:#f8fafc;color:#334155}
            .pd-blok-chip:hover{border-color:#cd6906;color:#cd6906;background:#fff8f0}

            .pd-sidebar{display:flex;flex-direction:column;gap:18px}
            .pd-file-list{max-height:200px;overflow-y:auto}
            .pd-file-item{display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid #f1f5f9}
            .pd-file-item:last-child{border-bottom:none}
            .pd-file-name{font-size:13px;font-weight:500;color:#0f172a}
            .pd-file-date{font-size:11px;color:#94a3b8}
            .pd-file-actions{display:flex;gap:4px}
            .pd-file-btn{width:30px;height:30px;border-radius:7px;border:none;background:transparent;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#94a3b8;font-size:13px;transition:all .12s}
            .pd-file-btn:hover{background:#fff8f0;color:#cd6906}
            .pd-file-btn.pd-danger:hover{background:#fef2f2;color:#dc2626}
            .pd-empty{text-align:center;padding:24px 0;color:#94a3b8;font-size:13px}

            .pd-table-card{background:#fff;border-radius:14px;box-shadow:0 1px 4px rgba(15,23,42,.07);overflow:hidden}
            .pd-table-head{padding:16px 20px;border-bottom:1px solid #f1f5f9;display:flex;align-items:center;justify-content:space-between}
            .pd-table{width:100%;border-collapse:collapse}
            .pd-table thead th{padding:10px 16px;font-size:11.5px;font-weight:600;color:#94a3b8;letter-spacing:.5px;text-align:left;border-bottom:1px solid #f1f5f9;background:#fafbfc}
            .pd-table tbody tr{transition:background .12s}
            .pd-table tbody tr:hover{background:#f0f5ff}
            .pd-table tbody td{padding:12px 16px;font-size:13px;color:#334155;border-bottom:1px solid #f8fafc}
            .pd-daire-no{font-weight:600;color:#0f172a}
            .pd-daire-sub{font-size:11px;color:#94a3b8}
            .pd-money{font-family:'Inter',monospace;font-weight:600;color:#0f172a;font-size:13px}
            .pd-edit-btn{width:32px;height:32px;border-radius:8px;border:none;background:transparent;cursor:pointer;color:#94a3b8;font-size:13px;transition:all .12s;opacity:0}
            .pd-row-hovered .pd-edit-btn{opacity:1}
            .pd-edit-btn:hover{background:#fff8f0;color:#cd6906}

            .pd-pag{display:flex;align-items:center;justify-content:center;gap:4px;padding:14px 20px;border-top:1px solid #f1f5f9}
            .pd-pag-btn{width:34px;height:34px;border-radius:8px;border:1px solid #e2e8f0;background:#fff;cursor:pointer;font-size:13px;font-weight:500;color:#64748b;display:flex;align-items:center;justify-content:center;transition:all .12s}
            .pd-pag-btn:hover:not(:disabled){border-color:#cd6906;color:#cd6906}
            .pd-pag-btn:disabled{opacity:.4;cursor:default}
            .pd-pag-btn.pd-pag-active{background:#cd6906;color:#fff;border-color:#cd6906}

            .pd-overlay{position:fixed;inset:0;background:rgba(15,23,42,.25);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:9999;animation:pdFadeIn .15s}
            @keyframes pdFadeIn{from{opacity:0}to{opacity:1}}
            .pd-modal{background:#fff;border-radius:16px;box-shadow:0 20px 60px rgba(15,23,42,.18);width:100%;max-height:90vh;overflow:hidden;display:flex;flex-direction:column;animation:pdSlideIn .2s}
            .pd-modal>form{display:flex;flex-direction:column;overflow:hidden;flex:1;min-height:0}
            .pd-modal-sm{max-width:440px}
            .pd-modal-md{max-width:560px}
            .pd-modal-lg{max-width:780px}
            .pd-modal-xl{max-width:960px}
            @keyframes pdSlideIn{from{transform:translateY(-12px);opacity:0}to{transform:translateY(0);opacity:1}}
            .pd-modal-head{padding:18px 24px;border-bottom:1px solid #f1f5f9;display:flex;align-items:center;justify-content:space-between}
            .pd-modal-title{font-size:16px;font-weight:700;color:#0f172a}
            .pd-modal-close{width:32px;height:32px;border-radius:8px;border:none;background:#f8fafc;cursor:pointer;font-size:16px;color:#64748b;display:flex;align-items:center;justify-content:center;transition:all .12s}
            .pd-modal-close:hover{background:#fee2e2;color:#dc2626}
            .pd-modal-body{padding:20px 24px;overflow-y:auto;flex:1;min-height:0}
            .pd-modal-foot{padding:14px 24px;border-top:1px solid #f1f5f9;display:flex;align-items:center;justify-content:flex-end;gap:8px}
            .pd-modal-foot-spread{justify-content:space-between}

            .pd-sablon-etiketler{margin-top:14px;border:1px solid #e2e8f0;border-radius:8px;background:#f8fafc}
            .pd-sablon-etiketler summary{cursor:pointer;padding:10px 12px;font-size:12.5px;font-weight:600;color:#334155;list-style:none;display:flex;align-items:center;gap:6px;user-select:none}
            .pd-sablon-etiketler summary::-webkit-details-marker{display:none}
            .pd-sablon-etiketler summary::before{content:'\\25B8';font-size:10px;color:#94a3b8;transition:transform .15s}
            .pd-sablon-etiketler[open] summary::before{transform:rotate(90deg)}
            .pd-sablon-etiketler-body{padding:0 12px 12px}
            .pd-sablon-etiketler-body p{margin:0 0 10px;font-size:11.5px;color:#64748b;line-height:1.5}
            .pd-sablon-etiket-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px 12px}
            .pd-sablon-etiket-item{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:6px 8px;background:#fff;border:1px solid #eef2f7;border-radius:6px}
            .pd-sablon-etiket-item code{font-size:11px;color:#b45309;background:#fffbeb;padding:2px 5px;border-radius:4px;white-space:nowrap}
            .pd-sablon-etiket-item span{font-size:11px;color:#64748b;text-align:right}

            .pd-form-row{display:grid;gap:14px;margin-bottom:14px}
            .pd-form-row-2{grid-template-columns:1fr 1fr}
            .pd-form-row-3{grid-template-columns:1fr 1fr 1fr}
            .pd-form-group label{display:block;font-size:12px;font-weight:600;color:#475569;margin-bottom:5px}
            .pd-input{width:100%;height:38px;padding:0 12px;border-radius:9px;border:1px solid #e2e8f0;font-size:13px;font-family:'Inter',sans-serif;color:#0f172a;transition:border-color .15s;background:#fff;box-sizing:border-box}
            .pd-input:focus{outline:none;border-color:#cd6906;box-shadow:0 0 0 3px rgba(205,105,6,.1)}
            .pd-textarea{height:auto;padding:10px 12px;resize:vertical}
            .pd-select{appearance:none;background-image:url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 12px center;padding-right:32px}
            .pd-switch-wrap{display:flex;align-items:center;gap:10px;margin-top:8px}
            .pd-switch{position:relative;width:40px;height:22px;flex-shrink:0}
            .pd-switch input{opacity:0;width:0;height:0}
            .pd-switch-slider{position:absolute;inset:0;background:#cbd5e1;border-radius:11px;cursor:pointer;transition:background .2s}
            .pd-switch-slider::before{content:'';position:absolute;left:3px;top:3px;width:16px;height:16px;background:#fff;border-radius:50%;transition:transform .2s}
            .pd-switch input:checked+.pd-switch-slider{background:#22c55e}
            .pd-switch input:checked+.pd-switch-slider::before{transform:translateX(18px)}
            .pd-switch-label{font-size:13px;font-weight:500;color:#334155}

            .pd-preview-content{text-align:center}
            .pd-preview-content embed{width:100%;height:70vh;border-radius:8px}
            .pd-preview-content img{max-width:100%;max-height:70vh;border-radius:8px}

            .pd-badge-durum{display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:600;padding:4px 11px;border-radius:20px}
        `}</style>

        <div className="pd-wrap">
            {/* ── HEADER ── */}
            <div className="pd-header">
                <div className="pd-title">
                    <button className="pd-back" onClick={() => navigate('/projeler')}>
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 12L6 8l4-4"/></svg>
                    </button>
                    {proje.projeAdi}
                </div>
                <div className="pd-actions">
                    <button className="pd-btn pd-btn-primary" onClick={() => setShowDaireModal(true)}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 1v12M1 7h12"/></svg>
                        {t('btn.yeniDaire')}
                    </button>
                    <button className="pd-btn pd-btn-ghost" style={{ border: '1px solid #16a34a', color: '#16a34a' }} onClick={() => { setExcelRows([]); setShowExcelModal(true); }}>
                        <i className="fas fa-file-excel" style={{ marginRight: 5 }}></i>
                        {t('btn.exceldenEkle')}
                    </button>
                    <div className="pd-dd-wrap" ref={ddRef}>
                        <button className="pd-btn pd-btn-ghost" onClick={() => setDdOpen(!ddOpen)} style={{ border: '1px solid #e2e8f0' }}>
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="7" cy="3" r="1"/><circle cx="7" cy="7" r="1"/><circle cx="7" cy="11" r="1"/></svg>
                            {t('label.islemler')}
                        </button>
                        <div className={`pd-dd-menu${ddOpen ? ' pd-dd-open' : ''}`}>
                            <button className="pd-dd-item" onClick={() => { setShowGorunumModal(true); setDdOpen(false); }}>
                                <i className="fas fa-layer-group"></i> {t('label.gorunumler')}
                            </button>
                            <button className="pd-dd-item" onClick={() => { fetchSunumBelgeler(); setShowSunumBelgeModal(true); setDdOpen(false); }}>
                                <i className="fas fa-images"></i> {t('label.sunumBelgeleri')}
                            </button>
                            <button className="pd-dd-item" onClick={() => { fetchProjeOzellikler(); fetchProjeOzellikOptions(); setShowProjeOzellikModal(true); setDdOpen(false); }}>
                                <i className="fas fa-list-alt"></i> {t('label.projeOzellikleri')}
                            </button>
                            <button className="pd-dd-item" onClick={() => { fetchImalatKategoriler(); setShowImalatKatModal(true); setDdOpen(false); }}>
                                <i className="fas fa-tools"></i> {t('label.imalatKategorileri')}
                            </button>
                            <button className="pd-dd-item" onClick={() => { setShowImalatRaporModal(true); fetchImalatRapor(); setDdOpen(false); }}>
                                <i className="fas fa-chart-bar"></i> {t('label.imalatRaporu')}
                            </button>
                            <div style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }} />
                            <button className="pd-dd-item" onClick={() => { setShowBlokModal(true); setDdOpen(false); }}>
                                <i className="fas fa-building"></i> {t('btn.blokEkle')}
                            </button>
                            <button className="pd-dd-item" onClick={() => { setShowDosyaModal(true); setDdOpen(false); }}>
                                <i className="fas fa-file-upload"></i> {t('btn.dosyaEkle')}
                            </button>
                            <button className="pd-dd-item" onClick={() => { setShowSablonModal(true); setDdOpen(false); }}>
                                <i className="fas fa-file-word"></i> {t('btn.sablonEkle')}
                            </button>
                            <button className="pd-dd-item" onClick={() => { setShowProjeSayfaModal(true); setDdOpen(false); }}>
                                <i className="fas fa-file-lines"></i> Proje Sayfası
                            </button>
                            <button className="pd-dd-item" onClick={() => { setShowFiyatlandirmaModal(true); setDdOpen(false); }}>
                                <i className="fas fa-tags"></i> Fiyatlandırma
                            </button>
                            <button className="pd-dd-item" onClick={() => { setShowSozlesmeOnayModal(true); setDdOpen(false); }}>
                                <i className="fas fa-user-check"></i> Sözleşme Onay Süreci
                            </button>
                            <button className="pd-dd-item" onClick={() => { openProjeDuzenle(); setDdOpen(false); }}>
                                <i className="fas fa-edit"></i> {t('btn.duzenle')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── PROJE KURULUM İLERLEMESİ ── */}
            {(() => {
                const adimlar: { etiket: string; tamam: boolean; aksiyon: () => void }[] = [
                    { etiket: 'Kat tanımlayın', tamam: katlar.length > 0, aksiyon: () => navigate('/ayarlar', { state: { grup: 0, item: 2 } }) },
                    { etiket: 'Daire tipi tanımlayın', tamam: daireTipleri.length > 0, aksiyon: () => navigate('/ayarlar', { state: { grup: 0, item: 1 } }) },
                    { etiket: 'Blok ekleyin', tamam: bloklar.length > 0, aksiyon: () => setShowBlokModal(true) },
                    { etiket: 'Daireleri oluşturun', tamam: daireler.length > 0, aksiyon: () => setShowDaireModal(true) },
                    { etiket: 'Daireler için görsel ekleyin', tamam: sunumBelgeler.length > 0, aksiyon: () => { fetchSunumBelgeler(); setShowSunumBelgeModal(true); } },
                    { etiket: 'Sözleşme şablonu ekleyin', tamam: sablonlar.length > 0, aksiyon: () => setShowSablonModal(true) },
                ];
                const tamamlanan = adimlar.filter(a => a.tamam).length;
                const yuzde = Math.round((tamamlanan / adimlar.length) * 100);
                if (tamamlanan === adimlar.length) return null;
                return (
                    <div style={{ background: '#fff', border: '1px solid #f1f5f9', borderRadius: 14, padding: '18px 22px', marginBottom: 22, boxShadow: '0 1px 4px rgba(15,23,42,0.05)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                                <i className="fas fa-rocket" style={{ color: '#cd6906', marginRight: 8 }}></i>
                                Proje Kurulumu — %{yuzde} tamamlandı
                            </div>
                            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{tamamlanan}/{adimlar.length} adım</span>
                        </div>
                        <div style={{ height: 6, background: '#f1f5f9', borderRadius: 9999, overflow: 'hidden', marginBottom: 14 }}>
                            <div style={{ width: `${yuzde}%`, height: '100%', background: '#cd6906', borderRadius: 9999, transition: 'width 0.3s' }} />
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {adimlar.filter(a => !a.tamam).map(a => (
                                <button key={a.etiket} type="button" onClick={a.aksiyon}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 9999, border: '1.5px solid #fed7aa', background: '#fff7ed', color: '#cd6906', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                                    <i className="fas fa-arrow-right" style={{ fontSize: 10 }}></i>
                                    {a.etiket}
                                </button>
                            ))}
                        </div>
                    </div>
                );
            })()}

            {/* ── KPI CARDS ── */}
            <div className="pd-stats">
                <div className="pd-stat">
                    <div className="pd-stat-label">{t('label.toplamDeger')}</div>
                    <div className="pd-stat-val">{formatMoney(totalValue)}</div>
                    <div className="pd-stat-sub">{daireler.length} {t('proje.daireAdet')}</div>
                </div>
                <div className="pd-stat">
                    <div className="pd-stat-label">{t('label.daireDurumu')}</div>
                    <div className="pd-stat-dots">
                        <span className="pd-stat-dot pd-sold">{sozlesme} {t('durum.sozlesme')}</span>
                        <span className="pd-stat-dot pd-reserved">{rezerve} {t('durum.rezerve')}</span>
                        <span className="pd-stat-dot pd-available">{satista} {t('durum.satista')}</span>
                    </div>
                </div>
                <div className="pd-stat">
                    <div className="pd-stat-label">{t('proje.bloklar')}</div>
                    <div className="pd-stat-val">{bloklar.length}</div>
                    <div className="pd-stat-sub">{bloklar.map(b=>b.blokAdi).join(', ') || '—'}</div>
                </div>
                <div className="pd-stat">
                    <div className="pd-stat-label">{t('label.projeDurumu')}</div>
                    <div style={{marginTop:4}}>
                        <span className="pd-pill" style={{background:pdm.bg,color:pdm.fg}}><span className="pd-pill-dot" style={{background:pdm.fg}}/>{proje.projeDurumu}</span>
                    </div>
                    <div className="pd-stat-sub">{proje.eklenmeTarihi ? new Date(proje.eklenmeTarihi).toLocaleDateString('tr-TR') : ''}</div>
                </div>
            </div>

            {/* ── INFO + SIDEBAR ── */}
            <div className="pd-grid">
                <div className="pd-card">
                    <div className="pd-card-head">
                        <span className="pd-card-title">{t('label.projeBilgileri')}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="pd-pill" style={{background: proje.aktifMi ? '#dcfce7' : '#fee2e2', color: proje.aktifMi ? '#15803d' : '#b91c1c'}}>
                                <span className="pd-pill-dot" style={{background: proje.aktifMi ? '#22c55e' : '#ef4444'}}/>{proje.aktifMi ? t('durum.aktif') : t('durum.pasif')}
                            </span>
                            <button
                                onClick={handleImalatAktifToggle}
                                disabled={imalatAktifToggling}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 10px', borderRadius: 20, border: '1px solid #e2e8f0', background: proje.imalatNotlariAktif ? '#eff6ff' : '#f8fafc', color: proje.imalatNotlariAktif ? '#1d4ed8' : '#64748b', cursor: 'pointer', fontWeight: 500 }}
                                title={t('label.imalatNotlariAktifPasif')}
                            >
                                <i className={`fas fa-tools`} style={{ fontSize: 11 }}></i>
                                {t('label.imalatNotlari')}: {proje.imalatNotlariAktif ? t('label.acik') : t('label.kapali')}
                            </button>
                        </div>
                    </div>
                    <div className="pd-card-body">
                        <div className="pd-proje-info">
                            <div className="pd-proje-logo">
                                {proje.projeLogo ? (
                                    <img src={`${API_BASE_URL}/userFiles/${proje.projeLogo}`} alt={proje.projeAdi} />
                                ) : (
                                    <svg width="32" height="32" fill="none" stroke="#94a3b8" strokeWidth="1.5"><rect x="4" y="8" width="24" height="20" rx="2"/><path d="M10 8V5a2 2 0 012-2h8a2 2 0 012 2v3"/></svg>
                                )}
                            </div>
                            <div className="pd-proje-meta">
                                <div className="pd-proje-name">{proje.projeAdi}</div>
                                <div className="pd-proje-loc">{proje.adres || ''} {proje.sehir ? `/ ${proje.sehir}` : ''}</div>
                                <div className="pd-detail-grid">
                                    <div className="pd-detail-item">{t('label.adaParsel')}: <span>{(proje.ada && proje.parsel) ? `${proje.ada} / ${proje.parsel}` : '—'}</span></div>
                                    <div className="pd-detail-item">{t('label.kdv')}: <span>%{proje.kdv}</span></div>
                                    <div className="pd-detail-item">{t('label.baslama')}: <span>{proje.baslamaTarihi ? new Date(proje.baslamaTarihi).toLocaleDateString('tr-TR') : '—'}</span></div>
                                    <div className="pd-detail-item">{t('label.bitis')}: <span>{proje.bitisTarihi ? new Date(proje.bitisTarihi).toLocaleDateString('tr-TR') : '—'}</span></div>
                                </div>
                            </div>
                        </div>
                        {bloklar.length > 0 && (
                            <div className="pd-bloklar">
                                <div className="pd-bloklar-title">{t('proje.bloklar')}</div>
                                <div className="pd-bloklar-list">
                                    {bloklar.map(blok => (
                                        <div key={blok.blokID} className="pd-blok-chip" onClick={() => { setSelectedBlok(blok); setBlokAdi(blok.blokAdi); setShowBlokEditModal(true); }}>
                                            {blok.blokAdi}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="pd-sidebar">
                    {/* Şablonlar */}
                    <div className="pd-card" style={{flex:1}}>
                        <div className="pd-card-head">
                            <span className="pd-card-title">{t('label.sablonlar')}</span>
                            <span style={{fontSize:11,color:'#94a3b8',fontWeight:600}}>{sablonlar.length}</span>
                        </div>
                        <div className="pd-card-body" style={{padding:'8px 16px'}}>
                            {sablonlar.length > 0 ? (
                                <div className="pd-file-list">
                                    {sablonlar.map(sablon => (
                                        <div key={sablon.dosyaID} className="pd-file-item">
                                            <div>
                                                <div className="pd-file-name">{sablon.aciklama}</div>
                                                <div className="pd-file-date">{new Date(sablon.eklenmeTarihi).toLocaleString('tr-TR')}</div>
                                            </div>
                                            <div className="pd-file-actions">
                                                <button className="pd-file-btn" onClick={() => window.open(`${API_BASE_URL}/api/Dosyalar/download/${sablon.dosyaID}`, '_blank')}><i className="fas fa-download"></i></button>
                                                <button className="pd-file-btn pd-danger" onClick={() => { setSelectedSablon(sablon); setShowDeleteModal(true); }}><i className="fas fa-trash"></i></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : <div className="pd-empty">{t('msg.henuzSablonEklenmemis')}</div>}
                        </div>
                    </div>
                    {/* Dosyalar */}
                    <div className="pd-card" style={{flex:1}}>
                        <div className="pd-card-head">
                            <span className="pd-card-title">{t('label.dosyalar')}</span>
                            <span style={{fontSize:11,color:'#94a3b8',fontWeight:600}}>{dosyalar.length}</span>
                        </div>
                        <div className="pd-card-body" style={{padding:'8px 16px'}}>
                            {dosyalar.length > 0 ? (
                                <div className="pd-file-list">
                                    {dosyalar.map(dosya => (
                                        <div key={dosya.dosyaID} className="pd-file-item">
                                            <div>
                                                <div className="pd-file-name">{dosya.aciklama}</div>
                                                <div className="pd-file-date">{new Date(dosya.eklenmeTarihi).toLocaleString('tr-TR')}</div>
                                            </div>
                                            <div className="pd-file-actions">
                                                {canPreviewFile(dosya.dosyaAdi) && (
                                                    <button className="pd-file-btn" onClick={async () => {
                                                        setSelectedDosya(dosya);
                                                        if (dosya.dosyaAdi.toLowerCase().endsWith('.pdf')) {
                                                            try {
                                                                const response = await axiosInstance.get(`/api/Dosyalar/download/${dosya.dosyaID}`, { params: { preview: true } });
                                                                setPdfData(`data:${response.data.mimeType};base64,${response.data.base64}`);
                                                            } catch { toast.error(t('msg.islemHatasi')); return; }
                                                        }
                                                        setShowPreviewModal(true);
                                                    }}><i className="fas fa-eye"></i></button>
                                                )}
                                                <button className="pd-file-btn" onClick={() => window.open(`${API_BASE_URL}/api/Dosyalar/download/${dosya.dosyaID}`, '_blank')}><i className="fas fa-download"></i></button>
                                                <button className="pd-file-btn pd-danger" onClick={() => { setSelectedDosya(dosya); setShowDeleteModal(true); }}><i className="fas fa-trash"></i></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : <div className="pd-empty">{t('msg.henuzDosyaEklenmemis')}</div>}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── DAIRE TABLE ── */}
            <div className="pd-table-card">
                <div className="pd-table-head">
                    <span className="pd-card-title">{t('label.daireListesi')}</span>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <button className="pd-btn pd-btn-ghost" style={{height:32,fontSize:12,padding:'0 12px'}} onClick={openTopluFiyatModal}>
                            <i className="fas fa-tags" style={{marginRight:5}}></i>{t('btn.topluFiyatGuncelle')}
                        </button>
                        <span style={{fontSize:12,color:'#94a3b8',fontWeight:600}}>
                            {hasActiveFilters ? `${filteredDaireler.length} / ${daireler.length} ${t('label.kayit')}` : `${daireler.length} ${t('label.kayit')}`}
                        </span>
                    </div>
                </div>
                {/* Filtreler */}
                <div style={{padding:'12px 20px',borderBottom:'1px solid #f1f5f9',background:'#fafbfc',display:'flex',flexWrap:'wrap',gap:8,alignItems:'center'}}>
                    <input
                        className="pd-input"
                        style={{width:130,height:34,fontSize:12,padding:'0 10px'}}
                        placeholder={t('placeholder.daireAdi')}
                        value={filterDaireNo}
                        onChange={e => { setFilterDaireNo(e.target.value); setCurrentPage(1); }}
                    />
                    <select className="pd-input" style={{width:120,height:34,fontSize:12,padding:'0 8px'}} value={filterBlokID} onChange={e => { setFilterBlokID(e.target.value === '' ? '' : Number(e.target.value)); setCurrentPage(1); }}>
                        <option value="">{t('label.tumBloklar')}</option>
                        {bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
                    </select>
                    <select className="pd-input" style={{width:120,height:34,fontSize:12,padding:'0 8px'}} value={filterKatID} onChange={e => { setFilterKatID(e.target.value === '' ? '' : Number(e.target.value)); setCurrentPage(1); }}>
                        <option value="">{t('label.tumKatlar')}</option>
                        {katlar.map(k => <option key={k.katID} value={k.katID}>{k.katAdi}</option>)}
                    </select>
                    <select className="pd-input" style={{width:130,height:34,fontSize:12,padding:'0 8px'}} value={filterCepheID} onChange={e => { setFilterCepheID(e.target.value === '' ? '' : Number(e.target.value)); setCurrentPage(1); }}>
                        <option value="">{t('label.tumCepheler')}</option>
                        {cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                    </select>
                    <select className="pd-input" style={{width:120,height:34,fontSize:12,padding:'0 8px'}} value={filterDaireTipID} onChange={e => { setFilterDaireTipID(e.target.value === '' ? '' : Number(e.target.value)); setCurrentPage(1); }}>
                        <option value="">Tüm Tipler</option>
                        {daireTipleri.map(tip => <option key={tip.daireTipID} value={tip.daireTipID}>{tip.tip}</option>)}
                    </select>
                    <select className="pd-input" style={{width:130,height:34,fontSize:12,padding:'0 8px'}} value={filterManzaraID} onChange={e => { setFilterManzaraID(e.target.value === '' ? '' : Number(e.target.value)); setCurrentPage(1); }}>
                        <option value="">{t('label.tumManzaralar')}</option>
                        {manzaralar.map(m => <option key={m.manzaraID} value={m.manzaraID}>{m.manzaraAdi}</option>)}
                    </select>
                    <select className="pd-input" style={{width:120,height:34,fontSize:12,padding:'0 8px'}} value={filterDurum} onChange={e => { setFilterDurum(e.target.value); setCurrentPage(1); }}>
                        <option value="">{t('label.tumDurumlar')}</option>
                        <option value="Satışta">{t('durum.satista')}</option>
                        <option value="Rezerve">{t('durum.rezerve')}</option>
                        <option value="Sözleşme">{t('durum.sozlesme')}</option>
                    </select>
                    <input
                        className="pd-input"
                        style={{width:120,height:34,fontSize:12,padding:'0 10px'}}
                        placeholder={t('placeholder.minFiyat')}
                        value={filterMinFiyat}
                        onChange={e => { setFilterMinFiyat(e.target.value); setCurrentPage(1); }}
                    />
                    <input
                        className="pd-input"
                        style={{width:120,height:34,fontSize:12,padding:'0 10px'}}
                        placeholder={t('placeholder.maxFiyat')}
                        value={filterMaxFiyat}
                        onChange={e => { setFilterMaxFiyat(e.target.value); setCurrentPage(1); }}
                    />
                    {hasActiveFilters && (
                        <button className="pd-btn pd-btn-ghost" style={{height:34,fontSize:12,padding:'0 12px'}} onClick={resetFilters}>
                            <i className="fas fa-times" style={{marginRight:4}}></i>{t('btn.temizle')}
                        </button>
                    )}
                </div>
                <table className="pd-table">
                    <thead>
                        <tr>
                            <th>{t('label.daire')}</th>
                            <th>{t('label.blok')}</th>
                            <th>{t('label.kat')}</th>
                            <th>{t('label.cephe')}</th>
                            <th>{t('label.manzara')}</th>
                            <th>{t('label.fiyat')}</th>
                            <th>{t('label.durum')}</th>
                            <th style={{width:50}}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {pagedDaireler.length > 0 ? pagedDaireler.map(d => {
                            const durumStyle = dm[d.daireDurumu] || { bg: '#f1f5f9', fg: '#475569', dot: '#94a3b8' };
                            return (
                                <tr key={d.daireID} className={hoveredRow === d.daireID ? 'pd-row-hovered' : ''} onMouseEnter={() => setHoveredRow(d.daireID)} onMouseLeave={() => setHoveredRow(null)}>
                                    <td>
                                        <div className="pd-daire-no">{d.daireNo}</div>
                                        {d.daireTipID > 0 && <div className="pd-daire-sub">{daireTipleri.find(item => item.daireTipID === d.daireTipID)?.tip || ''}</div>}
                                    </td>
                                    <td>{bloklar.find(b => b.blokID === d.blokID)?.blokAdi || '—'}</td>
                                    <td>{katlar.find(k => k.katID === d.katID)?.katAdi || '—'}</td>
                                    <td>{cepheler.find(c => c.cepheID === d.cepheID)?.cepheAdi || '—'}</td>
                                    <td>{manzaralar.find(m => m.manzaraID === d.manzaraID)?.manzaraAdi || '—'}</td>
                                    <td><span className="pd-money">{d.listeFiyati ? formatMoney(d.listeFiyati) : '—'}</span></td>
                                    <td>
                                        <span className="pd-badge-durum" style={{background: durumStyle.bg, color: durumStyle.fg}}>
                                            <span className="pd-pill-dot" style={{background: durumStyle.dot}}/>{d.daireDurumu}
                                        </span>
                                    </td>
                                    <td>
                                        <button className="pd-edit-btn" onClick={() => { setSelectedDaire(d); setShowDaireEditModal(true); }}>
                                            <i className="fas fa-pen"></i>
                                        </button>
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr><td colSpan={8} style={{textAlign:'center',padding:32,color:'#94a3b8'}}>{hasActiveFilters ? t('msg.filtreUygunDaireYok') : t('msg.henuzDaireEklenmemis')}</td></tr>
                        )}
                    </tbody>
                </table>
                {filteredTotalPages > 1 && (
                    <div className="pd-pag">
                        <button className="pd-pag-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>«</button>
                        <button className="pd-pag-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>‹</button>
                        {Array.from({ length: filteredTotalPages }, (_, i) => (
                            <button key={i+1} className={`pd-pag-btn${i+1 === currentPage ? ' pd-pag-active' : ''}`} onClick={() => setCurrentPage(i+1)}>{i+1}</button>
                        ))}
                        <button className="pd-pag-btn" disabled={currentPage === filteredTotalPages} onClick={() => setCurrentPage(p => p + 1)}>›</button>
                        <button className="pd-pag-btn" disabled={currentPage === filteredTotalPages} onClick={() => setCurrentPage(filteredTotalPages)}>»</button>
                    </div>
                )}
            </div>
        </div>

        {/* ═══ MODALS ═══ */}

        {/* Toplu Fiyat Güncelleme */}
        {showTopluFiyatModal && (() => {
            const gorünenler = topluGorunenler;
            const gorünenIDler = gorünenler.map(d => d.daireID);
            const tumüSeçili = gorünenIDler.length > 0 && gorünenIDler.every(id => topluSecili.has(id));
            // "Uygula" tam olarak bu daireleri değiştirir: filtreden geçen + seçili olanlar
            const uygulanacakSayisi = gorünenIDler.filter(id => topluSecili.has(id)).length;
            const degisecekSayisi = topluFiyatDaireler.filter(d =>
                topluSecili.has(d.daireID) && d.yeniFiyat !== null && d.yeniFiyat !== (d.listeFiyati ?? null)
            ).length;
            return (
                <div className="pd-overlay" onClick={() => setShowTopluFiyatModal(false)}>
                    <div className="pd-modal" onClick={e => e.stopPropagation()} style={{maxWidth:860,width:'96%',maxHeight:'92vh',display:'flex',flexDirection:'column'}}>
                        <div className="pd-modal-head">
                            <span className="pd-modal-title"><i className="fas fa-tags" style={{marginRight:8,color:'#cd6906'}}></i>{t('btn.topluFiyatGuncelle')}</span>
                            <button className="pd-modal-close" onClick={() => setShowTopluFiyatModal(false)}>✕</button>
                        </div>

                        {/* Uygulama Paneli */}
                        <div style={{padding:'12px 20px',borderBottom:'1px solid #f1f5f9',background:'#fffbf5',display:'flex',flexWrap:'wrap',gap:8,alignItems:'center'}}>
                            <select className="pd-input" style={{height:34,fontSize:12,padding:'0 8px',width:120}}
                                value={topluIslemTipi} onChange={e => setTopluIslemTipi(e.target.value as 'yuzde'|'sabit')}>
                                <option value="yuzde">{t('label.yuzde')}</option>
                                <option value="sabit">{t('label.sabitTutar')}</option>
                            </select>
                            <select className="pd-input" style={{height:34,fontSize:12,padding:'0 8px',width:100}}
                                value={topluIslemYon} onChange={e => setTopluIslemYon(e.target.value as 'artir'|'azalt')}>
                                <option value="artir">{t('label.artir')}</option>
                                <option value="azalt">{t('label.azalt')}</option>
                            </select>
                            <input className="pd-input" style={{height:34,fontSize:12,padding:'0 10px',width:130}}
                                placeholder={topluIslemTipi === 'yuzde' ? t('placeholder.yuzde') : t('label.tutar')}
                                value={topluIslemDeger}
                                onChange={e => setTopluIslemDeger(e.target.value)} />
                            <button className="pd-btn pd-btn-primary" style={{height:34,fontSize:12,padding:'0 16px'}}
                                disabled={uygulanacakSayisi === 0} onClick={topluUygula}>
                                {t('btn.uygula')}
                            </button>
                            <span style={{fontSize:12,color:'#94a3b8'}}>
                                {uygulanacakSayisi} {t('label.daireyeUygulanacak', 'daireye uygulanacak')}
                            </span>
                            <div style={{flex:1}}/>
                            {degisecekSayisi > 0 && (
                                <span style={{fontSize:12,fontWeight:600,color:'#cd6906',background:'#fff8f0',padding:'4px 12px',borderRadius:20,border:'1px solid #fde8cc'}}>
                                    {degisecekSayisi} {t('msg.dairedeFiyatDegisecek')}
                                </span>
                            )}
                        </div>

                        {/* Filtreler */}
                        <div style={{padding:'10px 20px',borderBottom:'1px solid #f1f5f9',background:'#fafbfc',display:'flex',flexWrap:'wrap',gap:8,alignItems:'center'}}>
                            <input className="pd-input" style={{width:130,height:32,fontSize:12,padding:'0 8px'}}
                                placeholder={t('placeholder.daireAdi')} value={topluFilterDaireNo}
                                onChange={e => setTopluFilterDaireNo(e.target.value)} />
                            <select className="pd-input" style={{width:110,height:32,fontSize:12,padding:'0 6px'}}
                                value={topluFilterBlok} onChange={e => setTopluFilterBlok(e.target.value === '' ? '' : Number(e.target.value))}>
                                <option value="">{t('label.tumBloklar')}</option>
                                {bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
                            </select>
                            <select className="pd-input" style={{width:110,height:32,fontSize:12,padding:'0 6px'}}
                                value={topluFilterKat} onChange={e => setTopluFilterKat(e.target.value === '' ? '' : Number(e.target.value))}>
                                <option value="">{t('label.tumKatlar')}</option>
                                {katlar.map(k => <option key={k.katID} value={k.katID}>{k.katAdi}</option>)}
                            </select>
                            <select className="pd-input" style={{width:120,height:32,fontSize:12,padding:'0 6px'}}
                                value={topluFilterCephe} onChange={e => setTopluFilterCephe(e.target.value === '' ? '' : Number(e.target.value))}>
                                <option value="">{t('label.tumCepheler')}</option>
                                {cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                            </select>
                            <select className="pd-input" style={{width:130,height:32,fontSize:12,padding:'0 6px'}}
                                value={topluFilterDaireTip} onChange={e => setTopluFilterDaireTip(e.target.value === '' ? '' : Number(e.target.value))}>
                                <option value="">{t('label.tumDaireTipleri', 'Tüm Daire Tipleri')}</option>
                                {daireTipleri.map(dt => <option key={dt.daireTipID} value={dt.daireTipID}>{dt.tip}</option>)}
                            </select>
                            <span style={{marginLeft:'auto',fontSize:12,color:'#94a3b8'}}>{gorünenler.length} / {topluFiyatDaireler.length} {t('label.daireGosteriliyor')}</span>
                        </div>

                        {/* Tablo */}
                        <div style={{overflowY:'auto',flex:1}}>
                            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                                <thead style={{position:'sticky',top:0,zIndex:2}}>
                                    <tr style={{background:'#f8fafc'}}>
                                        <th style={{padding:'10px 12px',textAlign:'center',width:42,borderBottom:'2px solid #e2e8f0'}}>
                                            <input type="checkbox" checked={tumüSeçili} onChange={e => {
                                                const yeni = new Set(topluSecili);
                                                if (e.target.checked) gorünenIDler.forEach(id => yeni.add(id));
                                                else gorünenIDler.forEach(id => yeni.delete(id));
                                                setTopluSecili(yeni);
                                            }} style={{width:15,height:15,cursor:'pointer'}} />
                                        </th>
                                        <th style={{padding:'10px 12px',textAlign:'left',fontWeight:600,color:'#64748b',fontSize:11.5,letterSpacing:.4,borderBottom:'2px solid #e2e8f0'}}>{t('label.daire').toUpperCase()}</th>
                                        <th style={{padding:'10px 12px',textAlign:'left',fontWeight:600,color:'#64748b',fontSize:11.5,letterSpacing:.4,borderBottom:'2px solid #e2e8f0'}}>{t('label.blok').toUpperCase()}</th>
                                        <th style={{padding:'10px 12px',textAlign:'left',fontWeight:600,color:'#64748b',fontSize:11.5,letterSpacing:.4,borderBottom:'2px solid #e2e8f0'}}>{t('label.kat').toUpperCase()}</th>
                                        <th style={{padding:'10px 12px',textAlign:'right',fontWeight:600,color:'#64748b',fontSize:11.5,letterSpacing:.4,borderBottom:'2px solid #e2e8f0'}}>{t('label.mevcutFiyat').toUpperCase()}</th>
                                        <th style={{padding:'10px 12px',textAlign:'right',fontWeight:600,color:'#64748b',fontSize:11.5,letterSpacing:.4,borderBottom:'2px solid #e2e8f0'}}>{t('label.yeniFiyat').toUpperCase()}</th>
                                        <th style={{padding:'10px 12px',textAlign:'right',fontWeight:600,color:'#64748b',fontSize:11.5,letterSpacing:.4,borderBottom:'2px solid #e2e8f0'}}>{t('label.fark').toUpperCase()}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gorünenler.length === 0 && (
                                        <tr><td colSpan={7} style={{textAlign:'center',padding:32,color:'#94a3b8'}}>{t('msg.filtreUygunDaireYok')}</td></tr>
                                    )}
                                    {gorünenler.map((d, i) => {
                                        const secili = topluSecili.has(d.daireID);
                                        const eski = d.listeFiyati ?? 0;
                                        const yeni = d.yeniFiyat ?? eski;
                                        const fark = yeni - eski;
                                        const degisti = fark !== 0;
                                        return (
                                            <tr key={d.daireID} style={{background: secili ? (i%2===0?'#fff':'#fafbfc') : '#f8f9fa', opacity: secili ? 1 : 0.5, borderBottom:'1px solid #f1f5f9'}}>
                                                <td style={{textAlign:'center',padding:'8px 12px'}}>
                                                    <input type="checkbox" checked={secili} onChange={e => {
                                                        const yeniSet = new Set(topluSecili);
                                                        if (e.target.checked) yeniSet.add(d.daireID);
                                                        else yeniSet.delete(d.daireID);
                                                        setTopluSecili(yeniSet);
                                                    }} style={{width:15,height:15,cursor:'pointer'}} />
                                                </td>
                                                <td style={{padding:'8px 12px',fontWeight:600,color:'#0f172a'}}>{d.daireNo}</td>
                                                <td style={{padding:'8px 12px',color:'#64748b'}}>{bloklar.find(b => b.blokID === d.blokID)?.blokAdi || '—'}</td>
                                                <td style={{padding:'8px 12px',color:'#64748b'}}>{katlar.find(k => k.katID === d.katID)?.katAdi || '—'}</td>
                                                <td style={{padding:'8px 12px',textAlign:'right',color:'#475569'}}>{eski ? formatMoney(eski) : '—'}</td>
                                                <td style={{padding:'8px 12px',textAlign:'right'}}>
                                                    <input
                                                        type="text"
                                                        value={yeni ? formatMoney(yeni) : ''}
                                                        onChange={e => {
                                                            const v = parseMoney(e.target.value);
                                                            setTopluFiyatDaireler(prev => prev.map(x => x.daireID === d.daireID ? {...x, yeniFiyat: v || null} : x));
                                                        }}
                                                        style={{width:130,textAlign:'right',border:'1px solid #e2e8f0',borderRadius:6,padding:'3px 8px',fontSize:12,background: degisti ? '#f0fdf4' : '#fff',color: degisti ? '#15803d' : '#0f172a',fontWeight: degisti ? 600 : 400}}
                                                    />
                                                </td>
                                                <td style={{padding:'8px 12px',textAlign:'right',fontWeight:600,fontSize:12,color: fark > 0 ? '#15803d' : fark < 0 ? '#dc2626' : '#94a3b8'}}>
                                                    {degisti ? (fark > 0 ? '+' : '') + formatMoney(fark) : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="pd-modal-foot">
                            <button type="button" className="pd-btn pd-btn-ghost" onClick={() => setShowTopluFiyatModal(false)}>{t('btn.iptal')}</button>
                            <button type="button" className="pd-btn pd-btn-primary" onClick={topluKaydet} disabled={topluSaving || degisecekSayisi === 0}>
                                {topluSaving ? t('msg.kaydediliyor') : `${degisecekSayisi > 0 ? degisecekSayisi + ' ' + t('label.daire') : ''} ${t('btn.kaydet')}`}
                            </button>
                        </div>
                    </div>
                </div>
            );
        })()}

        {/* Proje Düzenle */}
        {showModal && (
            <div className="pd-overlay" onClick={() => setShowModal(false)}>
                <div className="pd-modal pd-modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="pd-modal-head">
                        <span className="pd-modal-title">{t('label.projeDuzenle')}</span>
                        <button className="pd-modal-close" onClick={() => setShowModal(false)}>✕</button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="pd-modal-body">
                            <div className="pd-form-row pd-form-row-2">
                                <div className="pd-form-group">
                                    <label>{t('label.projeAdi')}</label>
                                    <input className="pd-input" type="text" value={formData?.projeAdi || ''} onChange={e => setFormData(p => p ? {...p, projeAdi: e.target.value} : null)} maxLength={50} required />
                                </div>
                                <div className="pd-form-group">
                                    <label>{t('label.sehir')}</label>
                                    <input className="pd-input" type="text" value={formData?.sehir || ''} onChange={e => setFormData(p => p ? {...p, sehir: e.target.value} : null)} maxLength={50} />
                                </div>
                            </div>
                            <div className="pd-form-row pd-form-row-2">
                                <div className="pd-form-group">
                                    <label>{t('label.ada')}</label>
                                    <input className="pd-input" type="text" value={formData?.ada || ''} onChange={e => setFormData(p => p ? {...p, ada: e.target.value} : null)} maxLength={50} />
                                </div>
                                <div className="pd-form-group">
                                    <label>{t('label.parsel')}</label>
                                    <input className="pd-input" type="text" value={formData?.parsel || ''} onChange={e => setFormData(p => p ? {...p, parsel: e.target.value} : null)} maxLength={50} />
                                </div>
                            </div>
                            <div className="pd-form-row pd-form-row-2">
                                <div className="pd-form-group">
                                    <label>{t('label.baslamaTarihi')}</label>
                                    <TrDateInput className="pd-input" value={formData?.baslamaTarihi?.split('T')[0] || ''} onChange={e => setFormData(p => p ? {...p, baslamaTarihi: e.target.value} : null)} />
                                </div>
                                <div className="pd-form-group">
                                    <label>{t('label.bitisTarihi')}</label>
                                    <TrDateInput className="pd-input" value={formData?.bitisTarihi?.split('T')[0] || ''} onChange={e => setFormData(p => p ? {...p, bitisTarihi: e.target.value} : null)} />
                                </div>
                            </div>
                            <div className="pd-form-row pd-form-row-2">
                                <div className="pd-form-group">
                                    <label>{t('label.adres')}</label>
                                    <textarea className="pd-input pd-textarea" rows={3} value={formData?.adres || ''} onChange={e => setFormData(p => p ? {...p, adres: e.target.value} : null)} maxLength={100} />
                                </div>
                                <div className="pd-form-group">
                                    <label>{t('label.projeLogo')}</label>
                                    <input className="pd-input" type="file" accept=".jpg,.jpeg,.png" onChange={handleFileChange} />
                                    <span style={{fontSize:11,color:'#94a3b8',marginTop:4,display:'block'}}>{t('msg.jpgVeyaPng')}</span>
                                </div>
                            </div>
                            <div className="pd-form-row pd-form-row-3">
                                <div className="pd-form-group">
                                    <label>{t('label.kdvYuzde')}</label>
                                    <input className="pd-input" type="number" value={formData?.kdv || 0} onChange={e => setFormData(p => p ? {...p, kdv: parseInt(e.target.value)} : null)} min={0} max={100} />
                                </div>
                                <div className="pd-form-group">
                                    <label>{t('label.projeDurumu')}</label>
                                    <select className="pd-input pd-select" value={formData?.projeDurumu || ''} onChange={e => setFormData(p => p ? {...p, projeDurumu: e.target.value} : null)}>
                                        <option value="Gelecek">{t('durum.gelecek')}</option>
                                        <option value="Devam Ediyor">{t('durum.devamEdiyor')}</option>
                                        <option value="Tamamlandı">{t('durum.tamamlandi')}</option>
                                    </select>
                                </div>
                                <div className="pd-form-group">
                                    <label>{t('label.durum')}</label>
                                    <div className="pd-switch-wrap">
                                        <label className="pd-switch">
                                            <input type="checkbox" checked={formData?.aktifMi || false} onChange={e => setFormData(p => p ? {...p, aktifMi: e.target.checked} : null)} />
                                            <span className="pd-switch-slider" />
                                        </label>
                                        <span className="pd-switch-label">{formData?.aktifMi ? t('durum.aktif') : t('durum.pasif')}</span>
                                    </div>
                                </div>
                            </div>

                            {/* ── Proje yöneticileri (birden fazla seçilebilir) ── */}
                            <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 4, paddingTop: 14, marginBottom: 14 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span style={{ fontSize: 13.5, fontWeight: 600, color: '#334155' }}>
                                        <i className="fas fa-user-tie" style={{ color: '#cd6906', marginRight: 7 }}></i>Proje Yöneticileri
                                    </span>
                                    {projeYoneticiIDler.length > 0 && (
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#cd6906' }}>{projeYoneticiIDler.length} kişi seçildi</span>
                                    )}
                                </div>
                                <div style={{ border: '1.5px solid #e5e7eb', borderRadius: 8, maxHeight: 180, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                                    {aktifKullanicilar.length === 0 ? (
                                        <div style={{ padding: 14, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Kullanıcılar yükleniyor...</div>
                                    ) : aktifKullanicilar.map(k => {
                                        const secili = projeYoneticiIDler.includes(k.kullaniciID);
                                        return (
                                            <label key={k.kullaniciID} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f8f9fa', background: secili ? '#fff7ed' : '#fff' }}>
                                                <input type="checkbox" checked={secili} onChange={() => toggleProjeYonetici(k.kullaniciID)} style={{ width: 14, height: 14, accentColor: '#cd6906' }} />
                                                <span style={{ fontSize: 13, color: '#374151' }}>{k.adSoyad}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* ── Projeye özel firma bilgileri ──
                                Kurumsal sayfasındaki genel firma bilgilerinden farklı
                                olması istendiğinde kullanılır. */}
                            <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 4, paddingTop: 14 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: '#334155' }}>
                                    <input type="checkbox"
                                        checked={formData?.firmaBilgileriAktif || false}
                                        onChange={e => setFormData(p => p ? { ...p, firmaBilgileriAktif: e.target.checked } : null)}
                                        style={{ width: 16, height: 16, accentColor: '#cd6906', cursor: 'pointer' }} />
                                    Firma Bilgilerini Gir
                                </label>
                                <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4, marginLeft: 25 }}>
                                    Bu projenin kurumsal bilgileri, Kurumsal sayfasındaki bilgilerden farklıysa doldurun.
                                </div>

                                {formData?.firmaBilgileriAktif && (
                                    <div style={{ marginTop: 14 }}>
                                        <div className="pd-form-row pd-form-row-2">
                                            <div className="pd-form-group">
                                                <label>Firma Adı</label>
                                                <input className="pd-input" type="text" maxLength={200}
                                                    value={formData?.firmaAdi || ''}
                                                    onChange={e => setFormData(p => p ? { ...p, firmaAdi: e.target.value } : null)} />
                                            </div>
                                            <div className="pd-form-group">
                                                <label>{t('label.telefon', 'Telefon')}</label>
                                                <input className="pd-input" type="text" maxLength={50}
                                                    value={formData?.firmaTelefon || ''}
                                                    onChange={e => setFormData(p => p ? { ...p, firmaTelefon: e.target.value } : null)} />
                                            </div>
                                        </div>
                                        <div className="pd-form-row">
                                            <div className="pd-form-group">
                                                <label>{t('label.adres')}</label>
                                                <textarea className="pd-input pd-textarea" rows={2} maxLength={500}
                                                    value={formData?.firmaAdres || ''}
                                                    onChange={e => setFormData(p => p ? { ...p, firmaAdres: e.target.value } : null)} />
                                            </div>
                                        </div>
                                        <div className="pd-form-row pd-form-row-2">
                                            <div className="pd-form-group">
                                                <label>Vergi Dairesi</label>
                                                <input className="pd-input" type="text" maxLength={100}
                                                    value={formData?.firmaVergiDairesi || ''}
                                                    onChange={e => setFormData(p => p ? { ...p, firmaVergiDairesi: e.target.value } : null)} />
                                            </div>
                                            <div className="pd-form-group">
                                                <label>Vergi Numarası</label>
                                                <input className="pd-input" type="text" maxLength={50}
                                                    value={formData?.firmaVergiNo || ''}
                                                    onChange={e => setFormData(p => p ? { ...p, firmaVergiNo: e.target.value } : null)} />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="pd-modal-foot">
                            <button type="button" className="pd-btn pd-btn-ghost" onClick={() => setShowModal(false)}>{t('btn.iptal')}</button>
                            <button type="submit" className="pd-btn pd-btn-primary">{t('btn.kaydet')}</button>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {/* Şablon Ekle */}
        {showSablonModal && (
            <div className="pd-overlay" onClick={() => setShowSablonModal(false)}>
                <div className="pd-modal pd-modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="pd-modal-head">
                        <span className="pd-modal-title">{t('btn.sablonEkle')}</span>
                        <button className="pd-modal-close" onClick={() => setShowSablonModal(false)}>✕</button>
                    </div>
                    <form onSubmit={handleSablonSubmit}>
                        <div className="pd-modal-body">
                            <div className="pd-form-group" style={{marginBottom:14}}>
                                <label>{t('label.sablonDosyasi')}</label>
                                <input className="pd-input" type="file" accept=".docx" onChange={handleSablonFileChange} required />
                                <span style={{fontSize:11,color:'#94a3b8',marginTop:4,display:'block'}}>{t('msg.sadeceDOCX')}</span>
                            </div>
                            <div className="pd-form-group">
                                <label>{t('label.aciklama')}</label>
                                <input className="pd-input" type="text" value={sablonAciklama} onChange={e => setSablonAciklama(e.target.value)} placeholder={t('placeholder.sablonAciklama')} required />
                            </div>
                            <details className="pd-sablon-etiketler">
                                <summary>{t('label.kullanilabilirEtiketler', 'Kullanılabilir etiketler')}</summary>
                                <div className="pd-sablon-etiketler-body">
                                    <p>{t('msg.sablonEtiketleriAciklama', 'Sözleşme indirilirken .docx içindeki bu etiketler otomatik olarak sözleşme bilgileriyle değiştirilir:')}</p>
                                    <div className="pd-sablon-etiket-grid">
                                        {[
                                            { tag: '[MUSTERIADI]', desc: 'Müşteri Adı Soyadı' },
                                            { tag: '[MUSTERITC]', desc: 'Müşteri TC Kimlik No' },
                                            { tag: '[MUSTERIADRES]', desc: 'Müşteri Adresi' },
                                            { tag: '[MUSTERITELEFON]', desc: 'Müşteri Telefon' },
                                            { tag: '[MUSTERIEPOSTA]', desc: 'Müşteri E-posta' },
                                            { tag: '[DAIRENO]', desc: 'Daire No' },
                                            { tag: '[DAIRETIPI]', desc: 'Daire Tipi' },
                                            { tag: '[BLOK]', desc: 'Blok Adı' },
                                            { tag: '[KAT]', desc: 'Kat Adı' },
                                            { tag: '[CEPHE]', desc: 'Cephe Adı' },
                                            { tag: '[MANZARA]', desc: 'Manzara Adı' },
                                            { tag: '[SOZLESMENO]', desc: 'Sözleşme No' },
                                            { tag: '[SOZLESMETARIHI]', desc: 'Sözleşme Tarihi' },
                                            { tag: '[SOZLESMEDENBIRGUNONCE]', desc: 'Sözleşmeden 1 gün önce' },
                                            { tag: '[SATISFIYATI]', desc: 'Satış Fiyatı' },
                                            { tag: '[VADELIODEMETUTAR]', desc: 'Vadeli Ödeme Tutarı' },
                                            { tag: '[FIYATYAZI]', desc: 'Satış Fiyatı (Yazıyla)' },
                                            { tag: '[PESINAT]', desc: 'Peşinat Toplamı' },
                                            { tag: '[KAPORA]', desc: 'Kapora Toplamı' },
                                            { tag: '[KALANODEME1]', desc: 'Kalan Ödeme (Satış Fiyatı − Kapora − Peşinat)' },
                                            { tag: '[EKSARTLAR]', desc: 'Özel Şartlar' },
                                            { tag: '[ODEMEPLANI]', desc: 'Ödeme Planı Tablosu' },
                                            { tag: '[TAKSITPLANI1]', desc: 'Ödemeler adetiyle (örn. 3x300.000TL)' },
                                            { tag: '[TAKSITPLANI2]', desc: 'Taksit planı, kapora/peşinat hariç' },
                                            { tag: '[SAYFASONU]', desc: 'Sayfayı burada bitir (sayfa kaymasını önler)' },
                                        ].map(e => (
                                            <div key={e.tag} className="pd-sablon-etiket-item">
                                                <code>{e.tag}</code>
                                                <span>{e.desc}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </details>
                        </div>
                        <div className="pd-modal-foot">
                            <button type="button" className="pd-btn pd-btn-ghost" onClick={() => setShowSablonModal(false)}>{t('btn.iptal')}</button>
                            <button type="submit" className="pd-btn pd-btn-primary" disabled={!sablonFile}>{t('btn.yukle')}</button>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {/* Proje Sayfası — tam genişlik yönetim modalı */}
        {showFiyatlandirmaModal && proje && (
            <ProjeFiyatlandirmaModal projeID={proje.projeID} onClose={() => setShowFiyatlandirmaModal(false)} />
        )}

        {showSozlesmeOnayModal && proje && (
            <ProjeSozlesmeOnayModal projeID={proje.projeID} onClose={() => setShowSozlesmeOnayModal(false)} />
        )}

        {showProjeSayfaModal && proje && (
            <ProjeSayfaYonetim projeID={proje.projeID} onClose={() => setShowProjeSayfaModal(false)} />
        )}

        {/* Dosya Ekle */}
        {showDosyaModal && (
            <div className="pd-overlay" onClick={() => setShowDosyaModal(false)}>
                <div className="pd-modal pd-modal-sm" onClick={e => e.stopPropagation()}>
                    <div className="pd-modal-head">
                        <span className="pd-modal-title">Dosya Ekle</span>
                        <button className="pd-modal-close" onClick={() => setShowDosyaModal(false)}>✕</button>
                    </div>
                    <form onSubmit={handleDosyaSubmit}>
                        <div className="pd-modal-body">
                            <div className="pd-form-group" style={{marginBottom:14}}>
                                <label>Dosya</label>
                                <input className="pd-input" type="file" onChange={handleDosyaFileChange} required />
                            </div>
                            <div className="pd-form-group">
                                <label>{t('label.aciklama')}</label>
                                <input className="pd-input" type="text" value={dosyaAciklama} onChange={e => setDosyaAciklama(e.target.value)} placeholder={t('label.aciklama')} required />
                            </div>
                        </div>
                        <div className="pd-modal-foot">
                            <button type="button" className="pd-btn pd-btn-ghost" onClick={() => setShowDosyaModal(false)}>{t('btn.iptal')}</button>
                            <button type="submit" className="pd-btn pd-btn-primary" disabled={!dosyaFile}>{t('btn.yukle')}</button>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {/* Sil Onay */}
        {showDeleteModal && (
            <div className="pd-overlay" onClick={() => setShowDeleteModal(false)}>
                <div className="pd-modal pd-modal-sm" onClick={e => e.stopPropagation()}>
                    <div className="pd-modal-head">
                        <span className="pd-modal-title">{selectedBlok ? 'Blok Sil' : selectedSablon ? 'Şablon Sil' : 'Dosya Sil'}</span>
                        <button className="pd-modal-close" onClick={() => setShowDeleteModal(false)}>✕</button>
                    </div>
                    <div className="pd-modal-body">
                        <p style={{fontSize:14,color:'#334155',margin:0}}>
                            {selectedBlok ? `"${selectedBlok.blokAdi}" bloğunu` : `"${(selectedSablon || selectedDosya)?.aciklama}" ${selectedSablon ? 'şablonunu' : 'dosyasını'}`} silmek istediğinize emin misiniz?
                        </p>
                    </div>
                    <div className="pd-modal-foot">
                        <button type="button" className="pd-btn pd-btn-ghost" onClick={() => setShowDeleteModal(false)}>{t('btn.iptal')}</button>
                        <button type="button" className="pd-btn" style={{background:'#dc2626',color:'#fff'}} onClick={handleDelete}>{t('btn.sil')}</button>
                    </div>
                </div>
            </div>
        )}

        {/* Önizleme */}
        {showPreviewModal && (
            <div className="pd-overlay" onClick={() => { setShowPreviewModal(false); setPdfData(null); }}>
                <div className="pd-modal pd-modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="pd-modal-head">
                        <span className="pd-modal-title">{selectedDosya?.aciklama}</span>
                        <button className="pd-modal-close" onClick={() => { setShowPreviewModal(false); setPdfData(null); }}>✕</button>
                    </div>
                    <div className="pd-modal-body">
                        {selectedDosya && (
                            <div className="pd-preview-content">
                                {selectedDosya.dosyaAdi.toLowerCase().endsWith('.pdf') ? (
                                    pdfData ? <embed src={pdfData} type="application/pdf" /> : <div style={{padding:40,color:'#94a3b8'}}>{t('msg.yukleniyor')}</div>
                                ) : (
                                    <img src={`${API_BASE_URL}/api/Dosyalar/download/${selectedDosya.dosyaID}`} alt={selectedDosya.aciklama} />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}

        {/* Blok Ekle */}
        {showBlokModal && (
            <div className="pd-overlay" onClick={() => setShowBlokModal(false)}>
                <div className="pd-modal pd-modal-sm" onClick={e => e.stopPropagation()}>
                    <div className="pd-modal-head">
                        <span className="pd-modal-title">Blok Ekle</span>
                        <button className="pd-modal-close" onClick={() => setShowBlokModal(false)}>✕</button>
                    </div>
                    <form onSubmit={handleBlokSubmit}>
                        <div className="pd-modal-body">
                            <div className="pd-form-group">
                                <label>{t('label.blokAdi')}</label>
                                <input className="pd-input" type="text" value={blokAdi} onChange={e => setBlokAdi(e.target.value)} placeholder={t('label.ara')} required />
                            </div>
                        </div>
                        <div className="pd-modal-foot">
                            <button type="button" className="pd-btn pd-btn-ghost" onClick={() => setShowBlokModal(false)}>{t('btn.iptal')}</button>
                            <button type="submit" className="pd-btn pd-btn-primary" disabled={!blokAdi.trim()}>{t('btn.ekle')}</button>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {/* Blok Düzenle */}
        {showBlokEditModal && (
            <div className="pd-overlay" onClick={() => { setShowBlokEditModal(false); setSelectedBlok(null); setBlokAdi(''); }}>
                <div className="pd-modal pd-modal-sm" onClick={e => e.stopPropagation()}>
                    <div className="pd-modal-head">
                        <span className="pd-modal-title">Blok Düzenle</span>
                        <button className="pd-modal-close" onClick={() => { setShowBlokEditModal(false); setSelectedBlok(null); setBlokAdi(''); }}>✕</button>
                    </div>
                    <form onSubmit={handleBlokEdit}>
                        <div className="pd-modal-body">
                            <div className="pd-form-group" style={{marginBottom:14}}>
                                <label>{t('label.blokAdi')}</label>
                                <input className="pd-input" type="text" value={blokAdi} onChange={e => setBlokAdi(e.target.value)} placeholder={t('label.ara')} required />
                            </div>
                            <div className="pd-switch-wrap">
                                <label className="pd-switch">
                                    <input type="checkbox" checked={selectedBlok?.aktifMi || false} onChange={e => setSelectedBlok(p => p ? {...p, aktifMi: e.target.checked} : null)} />
                                    <span className="pd-switch-slider" />
                                </label>
                                <span className="pd-switch-label">{t('durum.aktif')}</span>
                            </div>
                        </div>
                        <div className="pd-modal-foot pd-modal-foot-spread">
                            <button type="button" className="pd-btn" style={{background:'#dc2626',color:'#fff'}} onClick={() => { setShowBlokEditModal(false); setShowDeleteModal(true); }}>{t('btn.sil')}</button>
                            <div style={{display:'flex',gap:8}}>
                                <button type="button" className="pd-btn pd-btn-ghost" onClick={() => { setShowBlokEditModal(false); setSelectedBlok(null); setBlokAdi(''); }}>{t('btn.iptal')}</button>
                                <button type="submit" className="pd-btn pd-btn-primary" disabled={!blokAdi.trim()}>{t('btn.kaydet')}</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {/* Daire Ekle */}
        {showDaireModal && (
            <div className="pd-overlay" onClick={() => setShowDaireModal(false)}>
                <div className="pd-modal pd-modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="pd-modal-head">
                        <span className="pd-modal-title">{t('btn.daireEkle')}</span>
                        <button className="pd-modal-close" onClick={() => setShowDaireModal(false)}>✕</button>
                    </div>
                    <form onSubmit={handleDaireSubmit}>
                        <div className="pd-modal-body">
                            <div className="pd-form-row pd-form-row-3">
                                <div className="pd-form-group">
                                    <label>Daire No</label>
                                    <input className="pd-input" type="text" value={daire.daireNo || ''} onChange={e => setDaire({...daire, daireNo: e.target.value})} required />
                                </div>
                                <div className="pd-form-group">
                                    <label>Blok</label>
                                    <select className="pd-input pd-select" value={daire.blokID || ''} onChange={e => setDaire({...daire, blokID: Number(e.target.value)})} required>
                                        <option value="">{t('label.seciniz')}</option>
                                        {bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
                                    </select>
                                </div>
                                <div className="pd-form-group">
                                    <label>{t('label.daireTipi')}</label>
                                    <select className="pd-input pd-select" value={daire.daireTipID || ''} onChange={e => setDaire({...daire, daireTipID: Number(e.target.value)})} required>
                                        <option value="">{t('label.seciniz')}</option>
                                        {daireTipleri.map(item => <option key={item.daireTipID} value={item.daireTipID}>{item.tip}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="pd-form-row pd-form-row-3">
                                <div className="pd-form-group">
                                    <label>Kat</label>
                                    <select className="pd-input pd-select" value={daire.katID || ''} onChange={e => setDaire({...daire, katID: Number(e.target.value)})} required>
                                        <option value="">{t('label.seciniz')}</option>
                                        {katlar.map(k => <option key={k.katID} value={k.katID}>{k.katAdi}</option>)}
                                    </select>
                                </div>
                                <div className="pd-form-group">
                                    <label>Cephe</label>
                                    <select className="pd-input pd-select" value={daire.cepheID || ''} onChange={e => setDaire({...daire, cepheID: Number(e.target.value)})} required>
                                        <option value="">{t('label.seciniz')}</option>
                                        {cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                    </select>
                                </div>
                                <div className="pd-form-group">
                                    <label>Manzara</label>
                                    <select className="pd-input pd-select" value={daire.manzaraID || ''} onChange={e => setDaire({...daire, manzaraID: Number(e.target.value)})} required>
                                        <option value="">{t('label.seciniz')}</option>
                                        {manzaralar.map(m => <option key={m.manzaraID} value={m.manzaraID}>{m.manzaraAdi}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="pd-form-row pd-form-row-3">
                                <div className="pd-form-group">
                                    <label>Para Birimi</label>
                                    <select className="pd-input pd-select" value={daire.paraBirimiID || ''} onChange={e => setDaire({...daire, paraBirimiID: Number(e.target.value)})}>
                                        <option value="">{t('label.seciniz')}</option>
                                        {paraBirimleri.map(p => <option key={p.paraBirimiID} value={p.paraBirimiID}>{p.birim}</option>)}
                                    </select>
                                </div>
                                <div className="pd-form-group">
                                    <label>Yatay Sıra</label>
                                    <input className="pd-input" type="number" value={daire.yataySira || 0} onChange={e => setDaire({...daire, yataySira: Number(e.target.value)})} required />
                                </div>
                                <div className="pd-form-group">
                                    <label>Liste Fiyatı</label>
                                    <input className="pd-input" type="text" value={formatMoney(daire.listeFiyati)} onChange={e => setDaire({...daire, listeFiyati: parseMoney(e.target.value)})} onBlur={e => { e.target.value = formatMoney(parseMoney(e.target.value)); }} />
                                </div>
                            </div>
                            <div className="pd-form-row pd-form-row-2">
                                <div className="pd-form-group">
                                    <label>Net M²</label>
                                    <input className="pd-input" type="number" step="0.01" value={daire.netM2 ?? 0} onChange={e => setDaire({...daire, netM2: Number(e.target.value)})} />
                                </div>
                                <div className="pd-form-group">
                                    <label>Brüt M²</label>
                                    <input className="pd-input" type="number" step="0.01" value={daire.brutM2 ?? 0} onChange={e => setDaire({...daire, brutM2: Number(e.target.value)})} />
                                </div>
                            </div>
                            <div className="pd-form-group">
                                <label>{t('label.aciklama')}</label>
                                <textarea className="pd-input pd-textarea" rows={3} value={daire.aciklama || ''} onChange={e => setDaire({...daire, aciklama: e.target.value})} />
                            </div>
                            <div style={{marginTop:16,borderTop:'1px solid #e2e8f0',paddingTop:14}}>
                                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                                    <label style={{fontWeight:600,fontSize:14,color:'#334155',margin:0}}>Daire Özellikleri</label>
                                    <button type="button" className="pd-btn pd-btn-ghost" style={{fontSize:12,padding:'4px 10px'}}
                                        onClick={() => setDaireOzellikler([...daireOzellikler, {daireTakipNo:'', ozellik:'', deger:'', aktif:true}])}>
                                        + Özellik Ekle
                                    </button>
                                </div>
                                {daireOzellikler.map((oz, idx) => (
                                    <div key={idx} style={{display:'flex',gap:8,marginBottom:6,alignItems:'center'}}>
                                        <input className="pd-input" style={{flex:1}} placeholder={t('label.ara')} list="pd-ozellik-list"
                                            value={oz.ozellik}
                                            onChange={e => { const n=[...daireOzellikler]; n[idx]={...n[idx], ozellik:e.target.value}; setDaireOzellikler(n); }}
                                            onBlur={() => fetchDegerOptions(oz.ozellik)} />
                                        <input className="pd-input" style={{flex:1}} placeholder={t('label.ara')} list={`pd-deger-list-${idx}`}
                                            value={oz.deger}
                                            onChange={e => { const n=[...daireOzellikler]; n[idx]={...n[idx], deger:e.target.value}; setDaireOzellikler(n); }} />
                                        <button type="button" style={{background:'none',border:'none',color:'#dc2626',cursor:'pointer',fontSize:16,fontWeight:700,padding:'0 6px'}}
                                            onClick={() => setDaireOzellikler(daireOzellikler.filter((_,i) => i !== idx))}>✕</button>
                                        <datalist id={`pd-deger-list-${idx}`}>
                                            {(degerOptionsMap[oz.ozellik] || []).map(d => <option key={d} value={d} />)}
                                        </datalist>
                                    </div>
                                ))}
                                <datalist id="pd-ozellik-list">
                                    {ozellikOptions.map(o => <option key={o} value={o} />)}
                                </datalist>
                            </div>
                        </div>
                        <div className="pd-modal-foot">
                            <button type="button" className="pd-btn pd-btn-ghost" onClick={() => setShowDaireModal(false)}>{t('btn.iptal')}</button>
                            <button type="submit" className="pd-btn pd-btn-primary">{t('btn.kaydet')}</button>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {/* Excel Toplu Daire Ekleme */}
        {showExcelModal && (
            <div className="pd-overlay" onClick={() => setShowExcelModal(false)}>
                <div className="pd-modal" style={{ maxWidth: 900, width: '95vw' }} onClick={e => e.stopPropagation()}>
                    <div className="pd-modal-head">
                        <span className="pd-modal-title">Excel'den Toplu Daire Ekle</span>
                        <button className="pd-modal-close" onClick={() => setShowExcelModal(false)}>✕</button>
                    </div>
                    <div className="pd-modal-body">
                        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                            <button className="pd-btn pd-btn-ghost" style={{ border: '1px solid #16a34a', color: '#16a34a' }} onClick={downloadExcelTemplate}>
                                <i className="fas fa-download" style={{ marginRight: 5 }}></i>
                                Şablon İndir
                            </button>
                            <button className="pd-btn pd-btn-ghost" style={{ border: '1px solid #1a2340', color: '#1a2340' }} onClick={() => excelFileRef.current?.click()}>
                                <i className="fas fa-upload" style={{ marginRight: 5 }}></i>
                                Excel Yükle
                            </button>
                            <input ref={excelFileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleExcelUpload} />
                            {excelRows.length > 0 && (
                                <span style={{ fontSize: 13, color: '#64748b' }}>
                                    {excelRows.length} satır yüklendi — {excelRows.filter(r => r.hatalar.length === 0).length} geçerli, {excelRows.filter(r => r.hatalar.length > 0).length} hatalı
                                </span>
                            )}
                        </div>
                        {excelRows.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 14 }}>
                                Önce şablonu indirip doldurun, ardından Excel Yükle ile yükleyin.
                            </div>
                        )}
                        {excelRows.length > 0 && (
                            <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>#</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Daire No</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Blok</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Kat</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{t('label.tip')}</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Cephe</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Manzara</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Net m²</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Brüt m²</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Fiyat</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>{t('label.durum')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {excelRows.map((row, idx) => (
                                            <tr key={idx} style={{ background: row.hatalar.length > 0 ? '#fef2f2' : undefined }}>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9', color: '#94a3b8' }}>{idx + 1}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9', fontWeight: 600 }}>{row.daireNo || <span style={{ color: '#dc2626' }}>—</span>}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9' }}>{row.blokAdi}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9' }}>{row.katAdi}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9' }}>{row.daireTipi}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9' }}>{row.cephe}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9' }}>{row.manzara}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>{row.netM2}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>{row.brutM2}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>{row.listeFiyati}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9' }}>
                                                    {row.hatalar.length === 0
                                                        ? <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ Geçerli</span>
                                                        : <span style={{ color: '#dc2626', fontSize: 11 }} title={row.hatalar.join('\n')}>✕ {row.hatalar.join('; ')}</span>
                                                    }
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <div className="pd-modal-foot">
                        <button type="button" className="pd-btn pd-btn-ghost" onClick={() => setShowExcelModal(false)}>{t('btn.iptal')}</button>
                        <button
                            type="button"
                            className="pd-btn pd-btn-primary"
                            disabled={excelSaving || excelRows.filter(r => r.hatalar.length === 0).length === 0}
                            onClick={saveExcelDaireler}
                        >
                            {excelSaving ? 'Kaydediliyor...' : `${excelRows.filter(r => r.hatalar.length === 0).length} Daire Ekle`}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* Daire Düzenle */}
        {showDaireEditModal && selectedDaire && (
            <div className="pd-overlay" onClick={() => { setShowDaireEditModal(false); setSelectedDaire(null); }}>
                <div className="pd-modal pd-modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="pd-modal-head">
                        <span className="pd-modal-title">Daire Düzenle</span>
                        <button className="pd-modal-close" onClick={() => { setShowDaireEditModal(false); setSelectedDaire(null); }}>✕</button>
                    </div>
                    <form onSubmit={handleDaireEdit}>
                        <div className="pd-modal-body">
                            <div style={{marginBottom:14}}>
                                <span style={{fontSize:13,fontWeight:600,color:'#475569'}}>Daire Durumu: </span>
                                {(() => { const ds = dm[selectedDaire.daireDurumu] || {bg:'#f1f5f9',fg:'#475569',dot:'#94a3b8'}; return (
                                    <span className="pd-badge-durum" style={{background:ds.bg,color:ds.fg}}><span className="pd-pill-dot" style={{background:ds.dot}}/>{selectedDaire.daireDurumu}</span>
                                ); })()}
                            </div>
                            <div className="pd-form-row pd-form-row-3">
                                <div className="pd-form-group">
                                    <label>Daire No</label>
                                    <input className="pd-input" type="text" value={selectedDaire.daireNo || ''} onChange={e => setSelectedDaire(p => p ? {...p, daireNo: e.target.value} : null)} required />
                                </div>
                                <div className="pd-form-group">
                                    <label>Blok</label>
                                    <select className="pd-input pd-select" value={selectedDaire.blokID || ''} onChange={e => setSelectedDaire(p => p ? {...p, blokID: Number(e.target.value)} : null)} required>
                                        <option value="">{t('label.seciniz')}</option>
                                        {bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
                                    </select>
                                </div>
                                <div className="pd-form-group">
                                    <label>{t('label.daireTipi')}</label>
                                    <select className="pd-input pd-select" value={selectedDaire.daireTipID || ''} onChange={e => setSelectedDaire(p => p ? {...p, daireTipID: Number(e.target.value)} : null)} required>
                                        <option value="">{t('label.seciniz')}</option>
                                        {daireTipleri.map(item => <option key={item.daireTipID} value={item.daireTipID}>{item.tip}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="pd-form-row pd-form-row-3">
                                <div className="pd-form-group">
                                    <label>Kat</label>
                                    <select className="pd-input pd-select" value={selectedDaire.katID || ''} onChange={e => setSelectedDaire(p => p ? {...p, katID: Number(e.target.value)} : null)} required>
                                        <option value="">{t('label.seciniz')}</option>
                                        {katlar.map(k => <option key={k.katID} value={k.katID}>{k.katAdi}</option>)}
                                    </select>
                                </div>
                                <div className="pd-form-group">
                                    <label>Cephe</label>
                                    <select className="pd-input pd-select" value={selectedDaire.cepheID || ''} onChange={e => setSelectedDaire(p => p ? {...p, cepheID: Number(e.target.value)} : null)} required>
                                        <option value="">{t('label.seciniz')}</option>
                                        {cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                    </select>
                                </div>
                                <div className="pd-form-group">
                                    <label>Manzara</label>
                                    <select className="pd-input pd-select" value={selectedDaire.manzaraID || ''} onChange={e => setSelectedDaire(p => p ? {...p, manzaraID: Number(e.target.value)} : null)} required>
                                        <option value="">{t('label.seciniz')}</option>
                                        {manzaralar.map(m => <option key={m.manzaraID} value={m.manzaraID}>{m.manzaraAdi}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="pd-form-row pd-form-row-3">
                                <div className="pd-form-group">
                                    <label>Para Birimi</label>
                                    <select className="pd-input pd-select" value={selectedDaire.paraBirimiID || ''} onChange={e => setSelectedDaire(p => p ? {...p, paraBirimiID: Number(e.target.value)} : null)}>
                                        <option value="">{t('label.seciniz')}</option>
                                        {paraBirimleri.map(pb => <option key={pb.paraBirimiID} value={pb.paraBirimiID}>{pb.birim}</option>)}
                                    </select>
                                </div>
                                <div className="pd-form-group">
                                    <label>Yatay Sıra</label>
                                    <input className="pd-input" type="number" value={selectedDaire.yataySira || 0} onChange={e => setSelectedDaire(p => p ? {...p, yataySira: Number(e.target.value)} : null)} required />
                                </div>
                                <div className="pd-form-group">
                                    <label>Liste Fiyatı</label>
                                    <input className="pd-input" type="text" value={formatMoney(selectedDaire.listeFiyati)} onChange={e => { const v = parseMoney(e.target.value); setSelectedDaire(p => p ? {...p, listeFiyati: v} : null); }} onBlur={e => { e.target.value = formatMoney(parseMoney(e.target.value)); }} />
                                </div>
                            </div>
                            <div className="pd-form-row pd-form-row-3">
                                <div className="pd-form-group">
                                    <label>Net M²</label>
                                    <input className="pd-input" type="number" step="0.01" value={selectedDaire.netM2 ?? 0} onChange={e => setSelectedDaire(p => p ? {...p, netM2: Number(e.target.value)} : null)} />
                                </div>
                                <div className="pd-form-group">
                                    <label>Brüt M²</label>
                                    <input className="pd-input" type="number" step="0.01" value={selectedDaire.brutM2 ?? 0} onChange={e => setSelectedDaire(p => p ? {...p, brutM2: Number(e.target.value)} : null)} />
                                </div>
                                <div className="pd-form-group">
                                    <label>Durum</label>
                                    <div className="pd-switch-wrap">
                                        <label className="pd-switch">
                                            <input type="checkbox" checked={selectedDaire.aktifMi ?? false} onChange={e => {
                                                if (!e.target.checked && selectedDaire.daireDurumu !== 'Satışta') {
                                                    toast.error(t('msg.islemHatasi'));
                                                    return;
                                                }
                                                setSelectedDaire(p => p ? {...p, aktifMi: e.target.checked} : null);
                                            }} disabled={selectedDaire.daireDurumu !== 'Satışta'} />
                                            <span className="pd-switch-slider" />
                                        </label>
                                        <span className="pd-switch-label">{selectedDaire.aktifMi ? 'Aktif' : 'Pasif'}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="pd-form-group">
                                <label>{t('label.aciklama')}</label>
                                <textarea className="pd-input pd-textarea" rows={3} value={selectedDaire.aciklama || ''} onChange={e => setSelectedDaire(p => p ? {...p, aciklama: e.target.value} : null)} />
                            </div>

                            {/* Fiyat Geçmişi */}
                            {fiyatGecmisi.length > 0 && (
                                <div style={{marginTop:16,borderTop:'1px solid #e2e8f0',paddingTop:14}}>
                                    <label style={{fontWeight:600,fontSize:14,color:'#334155',display:'block',marginBottom:10}}>
                                        <i className="fas fa-history" style={{marginRight:6,color:'#cd6906'}}></i>Fiyat Geçmişi
                                    </label>
                                    <div style={{maxHeight:200,overflowY:'auto',borderRadius:8,border:'1px solid #e2e8f0'}}>
                                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                                            <thead>
                                                <tr style={{background:'#f8fafc'}}>
                                                    <th style={{padding:'7px 12px',textAlign:'left',fontWeight:600,color:'#64748b',borderBottom:'1px solid #e2e8f0'}}>{t('label.tarih')}</th>
                                                    <th style={{padding:'7px 12px',textAlign:'right',fontWeight:600,color:'#64748b',borderBottom:'1px solid #e2e8f0'}}>Eski Fiyat</th>
                                                    <th style={{padding:'7px 12px',textAlign:'right',fontWeight:600,color:'#64748b',borderBottom:'1px solid #e2e8f0'}}>Yeni Fiyat</th>
                                                    <th style={{padding:'7px 12px',textAlign:'left',fontWeight:600,color:'#64748b',borderBottom:'1px solid #e2e8f0'}}>Kullanıcı</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {fiyatGecmisi.map((fg, i) => (
                                                    <tr key={fg.fiyatGecmisID} style={{background: i % 2 === 0 ? '#fff' : '#fafbfc'}}>
                                                        <td style={{padding:'7px 12px',color:'#475569',borderBottom:'1px solid #f1f5f9'}}>
                                                            {new Date(fg.degisimTarihi).toLocaleString('tr-TR', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                                                        </td>
                                                        <td style={{padding:'7px 12px',textAlign:'right',color:'#dc2626',borderBottom:'1px solid #f1f5f9'}}>
                                                            {fg.eskiFiyat !== null ? formatMoney(fg.eskiFiyat) : '—'}
                                                        </td>
                                                        <td style={{padding:'7px 12px',textAlign:'right',color:'#15803d',fontWeight:600,borderBottom:'1px solid #f1f5f9'}}>
                                                            {fg.yeniFiyat !== null ? formatMoney(fg.yeniFiyat) : '—'}
                                                        </td>
                                                        <td style={{padding:'7px 12px',color:'#475569',borderBottom:'1px solid #f1f5f9'}}>
                                                            {fg.kullaniciAdi}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            <div style={{marginTop:16,borderTop:'1px solid #e2e8f0',paddingTop:14}}>
                                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                                    <label style={{fontWeight:600,fontSize:14,color:'#334155',margin:0}}>Daire Özellikleri</label>
                                    <button type="button" className="pd-btn pd-btn-ghost" style={{fontSize:12,padding:'4px 10px'}}
                                        onClick={() => setDaireOzellikler([...daireOzellikler, {daireTakipNo: selectedDaire.takipNo, ozellik:'', deger:'', aktif:true}])}>
                                        + Özellik Ekle
                                    </button>
                                </div>
                                {daireOzellikler.map((oz, idx) => (
                                    <div key={idx} style={{display:'flex',gap:8,marginBottom:6,alignItems:'center'}}>
                                        <input className="pd-input" style={{flex:1}} placeholder={t('label.ara')} list="pd-ozellik-list-edit"
                                            value={oz.ozellik}
                                            onChange={e => { const n=[...daireOzellikler]; n[idx]={...n[idx], ozellik:e.target.value}; setDaireOzellikler(n); }}
                                            onBlur={() => fetchDegerOptions(oz.ozellik)} />
                                        <input className="pd-input" style={{flex:1}} placeholder={t('label.ara')} list={`pd-deger-list-edit-${idx}`}
                                            value={oz.deger}
                                            onChange={e => { const n=[...daireOzellikler]; n[idx]={...n[idx], deger:e.target.value}; setDaireOzellikler(n); }} />
                                        <button type="button" style={{background:'none',border:'none',color:'#dc2626',cursor:'pointer',fontSize:16,fontWeight:700,padding:'0 6px'}}
                                            onClick={async () => {
                                                if (oz.daireOzellikId) {
                                                    try { await axiosInstance.delete(`/api/DaireOzellikler/${oz.daireOzellikId}`); } catch {}
                                                }
                                                setDaireOzellikler(daireOzellikler.filter((_,i) => i !== idx));
                                            }}>✕</button>
                                        <datalist id={`pd-deger-list-edit-${idx}`}>
                                            {(degerOptionsMap[oz.ozellik] || []).map(d => <option key={d} value={d} />)}
                                        </datalist>
                                    </div>
                                ))}
                                <datalist id="pd-ozellik-list-edit">
                                    {ozellikOptions.map(o => <option key={o} value={o} />)}
                                </datalist>
                            </div>

                            {/* Daire Sunum Belgeleri */}
                            <div style={{marginTop:16,borderTop:'1px solid #e2e8f0',paddingTop:14}}>
                                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                                    <label style={{fontWeight:600,fontSize:14,color:'#334155',margin:0}}>Daire Sunum Belgeleri</label>
                                    {!showDaireSunumEkleForm && (
                                        <button type="button" className="pd-btn pd-btn-ghost" style={{fontSize:12,padding:'4px 10px'}}
                                            onClick={() => setShowDaireSunumEkleForm(true)}>+ Belge Ekle</button>
                                    )}
                                </div>
                                {showDaireSunumEkleForm && (
                                    <div style={{background:'#f8fafc',borderRadius:8,padding:12,border:'1px solid #e2e8f0',marginBottom:12}}>
                                        <div style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap'}}>
                                            <div style={{flex:1,minWidth:140}}>
                                                <label className="pd-label" style={{fontSize:11}}>{t('label.aciklama')}</label>
                                                <input className="pd-input" placeholder={t('label.aciklama')} value={daireSunumForm.aciklama}
                                                    onChange={e => setDaireSunumForm(p => ({...p, aciklama: e.target.value}))} />
                                            </div>
                                            <div style={{minWidth:110}}>
                                                <label className="pd-label" style={{fontSize:11}}>Tür</label>
                                                <select className="pd-input" value={daireSunumForm.tur} style={{height:38}}
                                                    onChange={e => setDaireSunumForm(p => ({...p, tur: e.target.value}))}>
                                                    <option value="Görsel">Görsel</option>
                                                    <option value="Belge">Belge</option>
                                                    <option value="Video">Video</option>
                                                </select>
                                            </div>
                                        </div>
                                        {daireSunumForm.tur === 'Video' ? (
                                            <div style={{marginBottom:8}}>
                                                <label className="pd-label" style={{fontSize:11}}>Video Embed Kodu</label>
                                                <textarea className="pd-input" placeholder='<iframe src="..." allowfullscreen></iframe>' value={daireSunumForm.videoLink}
                                                    onChange={e => setDaireSunumForm(p => ({...p, videoLink: e.target.value}))} rows={3} style={{resize:'vertical',minHeight:50}} />
                                            </div>
                                        ) : (
                                            <div style={{marginBottom:8}}>
                                                <label className="pd-label" style={{fontSize:11}}>Dosya Seç</label>
                                                <input type="file" className="pd-input" style={{padding:6}}
                                                    accept={daireSunumForm.tur === 'Görsel' ? 'image/*' : '.pdf,.doc,.docx,.xls,.xlsx'}
                                                    onChange={e => setDaireSunumFile(e.target.files?.[0] || null)} />
                                            </div>
                                        )}
                                        <div style={{display:'flex',gap:6,justifyContent:'flex-end'}}>
                                            <button type="button" className="pd-btn pd-btn-ghost" style={{fontSize:12,padding:'4px 10px'}}
                                                onClick={() => {setShowDaireSunumEkleForm(false); setDaireSunumFile(null);}}>{t('btn.iptal')}</button>
                                            <button type="button" className="pd-btn pd-btn-primary" style={{fontSize:12,padding:'4px 10px'}}
                                                onClick={handleDaireSunumYukle} disabled={daireSunumSaving}>
                                                {daireSunumSaving ? t('msg.yukleniyor') : 'Yükle'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {daireSunumBelgeler.length === 0 && !showDaireSunumEkleForm && (
                                    <div style={{textAlign:'center',color:'#94a3b8',padding:'8px 0',fontSize:12}}>Henüz sunum belgesi eklenmemiş.</div>
                                )}
                                {daireSunumBelgeler.length > 0 && (
                                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(100px, 1fr))',gap:8}}>
                                        {daireSunumBelgeler.map(b => (
                                            <div key={b.sunumBelgeId} style={{border:'1px solid #e2e8f0',borderRadius:6,overflow:'hidden',background:'#fff',position:'relative'}}>
                                                <button type="button" onClick={() => handleDaireSunumSil(b.sunumBelgeId)}
                                                    style={{position:'absolute',top:2,right:2,background:'rgba(255,255,255,0.9)',border:'none',borderRadius:'50%',width:18,height:18,cursor:'pointer',color:'#dc2626',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',zIndex:2}}
                                                    title={t('btn.sil')}>✕</button>
                                                <div style={{height:70,display:'flex',alignItems:'center',justifyContent:'center',background:'#f8fafc',cursor:'pointer'}}
                                                    onClick={() => {
                                                        if (b.sunumBelgeTuru === 'Video') { setShowSunumPreview(b); }
                                                        else if (b.sunumBelgeTuru === 'Görsel') window.open(`${API_BASE_URL}/api/SunumBelgeleri/download/${b.sunumBelgeId}`, '_blank');
                                                        else window.open(`${API_BASE_URL}/api/SunumBelgeleri/download/${b.sunumBelgeId}`, '_blank');
                                                    }}>
                                                    {b.sunumBelgeTuru === 'Görsel' && (
                                                        <img src={`${API_BASE_URL}/api/SunumBelgeleri/thumbnail/${b.sunumBelgeId}`} alt={b.aciklama || ''}
                                                            style={{maxWidth:'100%',maxHeight:'100%',objectFit:'cover'}} />
                                                    )}
                                                    {b.sunumBelgeTuru === 'Belge' && (
                                                        <div style={{textAlign:'center',color:'#64748b'}}><i className="fas fa-file-alt" style={{fontSize:22}}></i><div style={{fontSize:9,marginTop:2}}>Belge</div></div>
                                                    )}
                                                    {b.sunumBelgeTuru === 'Video' && (
                                                        <div style={{textAlign:'center',color:'#64748b'}}><i className="fas fa-play-circle" style={{fontSize:22}}></i><div style={{fontSize:9,marginTop:2}}>Video</div></div>
                                                    )}
                                                </div>
                                                <div style={{padding:'4px 6px',borderTop:'1px solid #e2e8f0'}}>
                                                    <div style={{fontSize:10,fontWeight:600,color:'#334155',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                                                        {b.aciklama || b.sunumBelgeTuru}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="pd-modal-foot">
                            <button type="button" className="pd-btn pd-btn-ghost" onClick={() => { setShowDaireEditModal(false); setSelectedDaire(null); }}>{t('btn.iptal')}</button>
                            <button type="submit" className="pd-btn pd-btn-primary">{t('btn.kaydet')}</button>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {/* Sunum Belgeleri Modal */}
        {showSunumBelgeModal && (
            <div className="pd-overlay" onClick={() => { setShowSunumBelgeModal(false); setShowSunumEkleForm(false); setShowSunumPreview(null); }}>
                <div className="pd-modal pd-modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
                    <div className="pd-modal-head">
                        <h3 style={{ fontSize: 16 }}>🖼️ Sunum Belgeleri</h3>
                        <button className="pd-modal-close" onClick={() => { setShowSunumBelgeModal(false); setShowSunumEkleForm(false); setShowSunumPreview(null); }}>✕</button>
                    </div>
                    <div className="pd-modal-body">
                        {/* Önizleme */}
                        {showSunumPreview && (
                            <div style={{ marginBottom: 16, background: '#f8fafc', borderRadius: 8, padding: 12, border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                    <span style={{ fontWeight: 600, fontSize: 13 }}>{showSunumPreview.aciklama || 'Önizleme'}</span>
                                    <button onClick={() => setShowSunumPreview(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
                                </div>
                                {showSunumPreview.sunumBelgeTuru === 'Görsel' && (
                                    <img src={`${API_BASE_URL}/api/SunumBelgeleri/download/${showSunumPreview.sunumBelgeId}`} alt={showSunumPreview.aciklama || ''}
                                        style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 6 }} />
                                )}
                                {showSunumPreview.sunumBelgeTuru === 'Belge' && showSunumPreview.sunumBelgeAdi?.toLowerCase().endsWith('.pdf') && (
                                    <embed src={`${API_BASE_URL}/api/SunumBelgeleri/download/${showSunumPreview.sunumBelgeId}`} type="application/pdf" style={{ width: '100%', height: '50vh', borderRadius: 6 }} />
                                )}
                                {showSunumPreview.sunumBelgeTuru === 'Video' && showSunumPreview.sunumBelgeAdi && (
                                    <div style={{ borderRadius: 8, overflow: 'hidden' }}
                                        dangerouslySetInnerHTML={{ __html: showSunumPreview.sunumBelgeAdi }} />
                                )}
                            </div>
                        )}

                        {/* Yeni Ekleme Formu */}
                        {showSunumEkleForm ? (
                            <div style={{ background: '#f8fafc', borderRadius: 8, padding: 16, border: '1px solid #e2e8f0', marginBottom: 16 }}>
                                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Yeni Sunum Belgesi</div>
                                <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, minWidth: 180 }}>
                                        <label className="pd-label">{t('label.aciklama')}</label>
                                        <input className="pd-input" placeholder={t('label.aciklama')} value={sunumYeniForm.aciklama}
                                            onChange={e => setSunumYeniForm(p => ({ ...p, aciklama: e.target.value }))} />
                                    </div>
                                    <div style={{ minWidth: 130 }}>
                                        <label className="pd-label">Tür</label>
                                        <select className="pd-input" value={sunumYeniForm.tur}
                                            onChange={e => setSunumYeniForm(p => ({ ...p, tur: e.target.value }))} style={{ height: 38 }}>
                                            <option value="Görsel">Görsel</option>
                                            <option value="Belge">Belge</option>
                                            <option value="Video">Video</option>
                                        </select>
                                    </div>
                                </div>
                                {sunumYeniForm.tur === 'Video' ? (
                                    <div style={{ marginBottom: 10 }}>
                                        <label className="pd-label">Video Embed Kodu</label>
                                        <textarea className="pd-input" placeholder='<iframe src="..." allowfullscreen></iframe>' value={sunumYeniForm.videoLink}
                                            onChange={e => setSunumYeniForm(p => ({ ...p, videoLink: e.target.value }))} rows={3} style={{ resize: 'vertical', minHeight: 60 }} />
                                    </div>
                                ) : (
                                    <div style={{ marginBottom: 10 }}>
                                        <label className="pd-label">Dosya Seç</label>
                                        <input type="file" className="pd-input" style={{ padding: 6 }}
                                            accept={sunumYeniForm.tur === 'Görsel' ? 'image/*' : '.pdf,.doc,.docx,.xls,.xlsx'}
                                            onChange={e => setSunumFile(e.target.files?.[0] || null)} />
                                    </div>
                                )}
                                {/* Daire Seçimi */}
                                {(() => {
                                    const filtrelenenler = daireler.filter(d => {
                                        if (!d.aktifMi) return false;
                                        if (sunumDaireFilter.blokID && d.blokID.toString() !== sunumDaireFilter.blokID) return false;
                                        if (sunumDaireFilter.daireTipID && d.daireTipID.toString() !== sunumDaireFilter.daireTipID) return false;
                                        if (sunumDaireFilter.katID && d.katID.toString() !== sunumDaireFilter.katID) return false;
                                        if (sunumDaireFilter.cepheID && d.cepheID.toString() !== sunumDaireFilter.cepheID) return false;
                                        if (sunumDaireFilter.daireAdi && !d.daireNo.toLowerCase().includes(sunumDaireFilter.daireAdi.toLowerCase())) return false;
                                        return true;
                                    });
                                    return (
                                        <div style={{ marginBottom: 10 }}>
                                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Daire Seçimi</div>
                                            <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                                                <select style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4, flex: 1, minWidth: 90 }}
                                                    value={sunumDaireFilter.blokID} onChange={e => setSunumDaireFilter(p => ({ ...p, blokID: e.target.value }))}>
                                                    <option value="">Tüm Bloklar</option>
                                                    {bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
                                                </select>
                                                <select style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4, flex: 1, minWidth: 90 }}
                                                    value={sunumDaireFilter.daireTipID} onChange={e => setSunumDaireFilter(p => ({ ...p, daireTipID: e.target.value }))}>
                                                    <option value="">Tüm Tipler</option>
                                                    {daireTipleri.map(tip => <option key={tip.daireTipID} value={tip.daireTipID}>{tip.tip}</option>)}
                                                </select>
                                                <select style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4, flex: 1, minWidth: 80 }}
                                                    value={sunumDaireFilter.katID} onChange={e => setSunumDaireFilter(p => ({ ...p, katID: e.target.value }))}>
                                                    <option value="">Tüm Katlar</option>
                                                    {katlar.map(k => <option key={k.katID} value={k.katID}>{k.katAdi}</option>)}
                                                </select>
                                                <select style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4, flex: 1, minWidth: 80 }}
                                                    value={sunumDaireFilter.cepheID} onChange={e => setSunumDaireFilter(p => ({ ...p, cepheID: e.target.value }))}>
                                                    <option value="">Tüm Cepheler</option>
                                                    {cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                                </select>
                                                <input style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4, flex: 1, minWidth: 90 }}
                                                    placeholder="Daire adı..." value={sunumDaireFilter.daireAdi}
                                                    onChange={e => setSunumDaireFilter(p => ({ ...p, daireAdi: e.target.value }))} />
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                <button type="button" style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #cbd5e1', borderRadius: 4, background: '#f8fafc', cursor: 'pointer' }}
                                                    onClick={() => {
                                                        const yeni = Array.from(new Set(sunumSeciliTakipNolar.concat(filtrelenenler.map(d => d.takipNo))));
                                                        setSunumSeciliTakipNolar(yeni);
                                                    }}>Tümünü Seç</button>
                                                <button type="button" style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #cbd5e1', borderRadius: 4, background: '#f8fafc', cursor: 'pointer' }}
                                                    onClick={() => {
                                                        const filtreSet = new Set(filtrelenenler.map(d => d.takipNo));
                                                        setSunumSeciliTakipNolar(sunumSeciliTakipNolar.filter(tn => !filtreSet.has(tn)));
                                                    }}>Seçimi Kaldır</button>
                                                <span style={{ fontSize: 11, color: '#64748b', marginLeft: 'auto' }}>
                                                    {filtrelenenler.filter(d => sunumSeciliTakipNolar.includes(d.takipNo)).length}/{filtrelenenler.length} daire seçili
                                                </span>
                                            </div>
                                            <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                                                {filtrelenenler.length === 0 ? (
                                                    <div style={{ padding: 12, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>Daire bulunamadı</div>
                                                ) : filtrelenenler.map(d => {
                                                    const secili = sunumSeciliTakipNolar.includes(d.takipNo);
                                                    return (
                                                        <label key={d.daireID} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: secili ? '#eff6ff' : 'transparent' }}>
                                                            <input type="checkbox" checked={secili} onChange={() => {
                                                                setSunumSeciliTakipNolar(prev =>
                                                                    secili ? prev.filter(t => t !== d.takipNo) : [...prev, d.takipNo]
                                                                );
                                                            }} style={{ accentColor: '#3b82f6' }} />
                                                            <span style={{ fontSize: 13, fontWeight: 500, minWidth: 60 }}>{d.daireNo}</span>
                                                            <span style={{ fontSize: 11, color: '#64748b', flex: 1 }}>
                                                                {bloklar.find(b => b.blokID === d.blokID)?.blokAdi}
                                                                {katlar.find(k => k.katID === d.katID) ? ` · ${katlar.find(k => k.katID === d.katID)?.katAdi}` : ''}
                                                                {cepheler.find(c => c.cepheID === d.cepheID) ? ` · ${cepheler.find(c => c.cepheID === d.cepheID)?.cepheAdi}` : ''}
                                                            </span>
                                                            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: d.daireDurumu === 'Satışta' ? '#dcfce7' : d.daireDurumu === 'Satıldı' ? '#fee2e2' : '#fef3c7', color: d.daireDurumu === 'Satışta' ? '#15803d' : d.daireDurumu === 'Satıldı' ? '#b91c1c' : '#92400e' }}>
                                                                {d.daireDurumu}
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                <button className="pd-btn pd-btn-ghost" onClick={() => { setShowSunumEkleForm(false); setSunumFile(null); setSunumSeciliTakipNolar([]); setSunumDaireFilter({ blokID: '', daireTipID: '', katID: '', cepheID: '', daireAdi: '' }); }}>{t('btn.iptal')}</button>
                                                <button className="pd-btn pd-btn-primary" onClick={handleSunumBelgeYukle} disabled={sunumBelgeSaving || filtrelenenler.filter((d: Daire) => sunumSeciliTakipNolar.includes(d.takipNo)).length === 0}>
                                                    {sunumBelgeSaving ? t('msg.yukleniyor') : 'Yükle'}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        ) : (
                            <div style={{ marginBottom: 16 }}>
                                <button className="pd-btn pd-btn-primary" style={{ fontSize: 13 }} onClick={() => {
                                    setSunumDaireFilter({ blokID: '', daireTipID: '', katID: '', cepheID: '', daireAdi: '' });
                                    setSunumSeciliTakipNolar(daireler.filter(d => d.aktifMi).map(d => d.takipNo));
                                    setShowSunumEkleForm(true);
                                }}>
                                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 1v10M1 6h10"/></svg>
                                    Yeni Ekle
                                </button>
                            </div>
                        )}

                        {/* Mevcut Belgeler Listesi — gruplandırılmış */}
                        {(() => {
                            const gruplari = new Map<string, { aciklama: string | null; sunumBelgeTuru: string; ilkBelgeId: number; kayitlar: SunumBelge[] }>();
                            sunumBelgeler.forEach(b => {
                                const key = b.sunumBelgeAdi || `video-${b.sunumBelgeId}`;
                                if (!gruplari.has(key)) gruplari.set(key, { aciklama: b.aciklama || null, sunumBelgeTuru: b.sunumBelgeTuru, ilkBelgeId: b.sunumBelgeId, kayitlar: [] });
                                gruplari.get(key)!.kayitlar.push(b);
                            });
                            const grupListesi = Array.from(gruplari.entries()).map(([key, val]) => ({ sunumBelgeAdi: key, ...val, daireTakipNolar: val.kayitlar.map(b => b.takipNo) }));
                            if (grupListesi.length === 0) return <div style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0', fontSize: 13 }}>Henüz sunum belgesi eklenmemiş.</div>;
                            // Sıra işlemleri için sadeleştirilmiş liste (API zaten Sira'ya göre döndürüyor)
                            const siraListesi = grupListesi.map(g => ({ sunumBelgeAdi: g.sunumBelgeAdi, ilkBelgeId: g.ilkBelgeId }));
                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>
                                        <i className="fas fa-grip-vertical" style={{ marginRight: 5, opacity: .6 }}></i>
                                        Sıra kutusuna yeni değer yazarak veya satırı sürükleyerek sıralayabilirsiniz. Daire detayındaki görseller bu sıraya göre gösterilir.
                                    </div>
                                    {grupListesi.map((grup, grupIdx) => {
                                        const label = computeDaireLabel(grup.daireTakipNolar);
                                        const isEditing = editBelgeAdi === grup.sunumBelgeAdi;
                                        return (
                                            <div key={grup.sunumBelgeAdi}
                                                draggable
                                                onDragStart={() => setSuruklenenGrupIdx(grupIdx)}
                                                onDragOver={e => { e.preventDefault(); if (uzerindeGrupIdx !== grupIdx) setUzerindeGrupIdx(grupIdx); }}
                                                onDragLeave={() => setUzerindeGrupIdx(p => p === grupIdx ? null : p)}
                                                onDrop={e => {
                                                    e.preventDefault();
                                                    if (suruklenenGrupIdx !== null) grupTasi(siraListesi, suruklenenGrupIdx, grupIdx);
                                                    setSuruklenenGrupIdx(null); setUzerindeGrupIdx(null);
                                                }}
                                                onDragEnd={() => { setSuruklenenGrupIdx(null); setUzerindeGrupIdx(null); }}
                                                style={{
                                                    border: uzerindeGrupIdx === grupIdx && suruklenenGrupIdx !== null && suruklenenGrupIdx !== grupIdx
                                                        ? '1px solid #cd6906' : '1px solid #e2e8f0',
                                                    borderRadius: 8, overflow: 'hidden', background: '#fff',
                                                    opacity: suruklenenGrupIdx === grupIdx ? 0.45 : 1,
                                                }}>
                                                {/* Satır */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                                        <i className="fas fa-grip-vertical" style={{ color: '#cbd5e1', fontSize: 12, cursor: 'grab' }} title="Sürükleyerek taşı"></i>
                                                        <input type="number" min={1} max={grupListesi.length}
                                                            defaultValue={grupIdx + 1}
                                                            key={`sira-${grup.sunumBelgeAdi}-${grupIdx}`}
                                                            title="Sıra"
                                                            onClick={e => e.stopPropagation()}
                                                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                                            onBlur={e => {
                                                                const yeni = parseInt(e.target.value, 10);
                                                                if (isNaN(yeni) || yeni < 1 || yeni > grupListesi.length || yeni - 1 === grupIdx) {
                                                                    e.target.value = String(grupIdx + 1);
                                                                    return;
                                                                }
                                                                grupTasi(siraListesi, grupIdx, yeni - 1);
                                                            }}
                                                            style={{ width: 44, fontSize: 12, textAlign: 'center', padding: '3px 2px', border: '1px solid #e2e8f0', borderRadius: 4, color: '#475569' }} />
                                                    </div>
                                                    <div style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 6, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'pointer' }}
                                                        onClick={() => setShowSunumPreview(grup.kayitlar[0])}>
                                                        {grup.sunumBelgeTuru === 'Görsel' && <img src={`${API_BASE_URL}/api/SunumBelgeleri/thumbnail/${grup.ilkBelgeId}`} alt={grup.aciklama || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                                        {grup.sunumBelgeTuru === 'Belge' && <i className="fas fa-file-alt" style={{ fontSize: 20, color: '#64748b' }}></i>}
                                                        {grup.sunumBelgeTuru === 'Video' && <i className="fas fa-play-circle" style={{ fontSize: 20, color: '#64748b' }}></i>}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {grup.aciklama || grup.sunumBelgeTuru}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={label}>
                                                            <span style={{ background: '#e2e8f0', borderRadius: 10, padding: '1px 6px', fontSize: 10, marginRight: 6 }}>{grup.sunumBelgeTuru}</span>
                                                            {label}
                                                            <span style={{ marginLeft: 4, color: '#94a3b8' }}>({grup.daireTakipNolar.length})</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                                        <button style={{ fontSize: 11, padding: '3px 10px', border: `1px solid #3b82f6`, borderRadius: 4, background: isEditing ? '#3b82f6' : '#fff', color: isEditing ? '#fff' : '#3b82f6', cursor: 'pointer' }}
                                                            onClick={() => {
                                                                if (isEditing) { setEditBelgeAdi(null); }
                                                                else { setEditBelgeAdi(grup.sunumBelgeAdi); setEditGrupTakipNolar([...grup.daireTakipNolar]); setEditGrupFilter({ blokID: '', daireTipID: '', katID: '', cepheID: '', daireAdi: '' }); }
                                                            }}>Düzenle</button>
                                                        <button style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #dc2626', borderRadius: 4, background: '#fff', color: '#dc2626', cursor: 'pointer' }}
                                                            onClick={() => handleBelgeGrubuSil(grup.kayitlar)}>Sil</button>
                                                    </div>
                                                </div>
                                                {/* Düzenleme paneli */}
                                                {isEditing && (() => {
                                                    const filtrelenenler = daireler.filter(d => {
                                                        if (!d.aktifMi) return false;
                                                        if (editGrupFilter.blokID && d.blokID.toString() !== editGrupFilter.blokID) return false;
                                                        if (editGrupFilter.daireTipID && d.daireTipID.toString() !== editGrupFilter.daireTipID) return false;
                                                        if (editGrupFilter.katID && d.katID.toString() !== editGrupFilter.katID) return false;
                                                        if (editGrupFilter.cepheID && d.cepheID.toString() !== editGrupFilter.cepheID) return false;
                                                        if (editGrupFilter.daireAdi && !d.daireNo.toLowerCase().includes(editGrupFilter.daireAdi.toLowerCase())) return false;
                                                        return true;
                                                    });
                                                    return (
                                                        <div style={{ borderTop: '1px solid #e2e8f0', background: '#f8fafc', padding: 12 }}>
                                                            <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                                                                <select style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4, flex: 1, minWidth: 80 }} value={editGrupFilter.blokID} onChange={e => setEditGrupFilter(p => ({ ...p, blokID: e.target.value }))}>
                                                                    <option value="">Tüm Bloklar</option>
                                                                    {bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
                                                                </select>
                                                                <select style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4, flex: 1, minWidth: 80 }} value={editGrupFilter.daireTipID} onChange={e => setEditGrupFilter(p => ({ ...p, daireTipID: e.target.value }))}>
                                                                    <option value="">Tüm Tipler</option>
                                                                    {daireTipleri.map(tip => <option key={tip.daireTipID} value={tip.daireTipID}>{tip.tip}</option>)}
                                                                </select>
                                                                <select style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4, flex: 1, minWidth: 75 }} value={editGrupFilter.katID} onChange={e => setEditGrupFilter(p => ({ ...p, katID: e.target.value }))}>
                                                                    <option value="">Tüm Katlar</option>
                                                                    {katlar.map(k => <option key={k.katID} value={k.katID}>{k.katAdi}</option>)}
                                                                </select>
                                                                <select style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4, flex: 1, minWidth: 75 }} value={editGrupFilter.cepheID} onChange={e => setEditGrupFilter(p => ({ ...p, cepheID: e.target.value }))}>
                                                                    <option value="">Tüm Cepheler</option>
                                                                    {cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                                                </select>
                                                                <input style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4, flex: 1, minWidth: 80 }} placeholder="Daire adı..." value={editGrupFilter.daireAdi} onChange={e => setEditGrupFilter(p => ({ ...p, daireAdi: e.target.value }))} />
                                                            </div>
                                                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                                                                <button type="button" style={{ fontSize: 10, padding: '2px 8px', border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
                                                                    onClick={() => setEditGrupTakipNolar(prev => Array.from(new Set(prev.concat(filtrelenenler.map(d => d.takipNo)))))}>Tümünü Seç</button>
                                                                <button type="button" style={{ fontSize: 10, padding: '2px 8px', border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
                                                                    onClick={() => { const s = new Set(filtrelenenler.map(d => d.takipNo)); setEditGrupTakipNolar(prev => prev.filter(tn => !s.has(tn))); }}>Seçimi Kaldır</button>
                                                                <span style={{ fontSize: 10, color: '#64748b', marginLeft: 'auto' }}>{editGrupTakipNolar.length} / {daireler.filter(d => d.aktifMi).length} seçili</span>
                                                            </div>
                                                            <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 8 }}>
                                                                {filtrelenenler.length === 0
                                                                    ? <div style={{ padding: 10, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>Daire bulunamadı</div>
                                                                    : filtrelenenler.map(d => {
                                                                        const secili = editGrupTakipNolar.includes(d.takipNo);
                                                                        return (
                                                                            <label key={d.daireID} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: secili ? '#eff6ff' : 'transparent' }}>
                                                                                <input type="checkbox" checked={secili} onChange={() => setEditGrupTakipNolar(prev => secili ? prev.filter(tn => tn !== d.takipNo) : [...prev, d.takipNo])} style={{ accentColor: '#3b82f6' }} />
                                                                                <span style={{ fontSize: 12, fontWeight: 500, minWidth: 55 }}>{d.daireNo}</span>
                                                                                <span style={{ fontSize: 10, color: '#64748b', flex: 1 }}>
                                                                                    {bloklar.find(b => b.blokID === d.blokID)?.blokAdi}
                                                                                    {katlar.find(k => k.katID === d.katID) ? ` · ${katlar.find(k => k.katID === d.katID)?.katAdi}` : ''}
                                                                                    {cepheler.find(c => c.cepheID === d.cepheID) ? ` · ${cepheler.find(c => c.cepheID === d.cepheID)?.cepheAdi}` : ''}
                                                                                </span>
                                                                                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: d.daireDurumu === 'Satışta' ? '#dcfce7' : d.daireDurumu === 'Satıldı' ? '#fee2e2' : '#fef3c7', color: d.daireDurumu === 'Satışta' ? '#15803d' : d.daireDurumu === 'Satıldı' ? '#b91c1c' : '#92400e' }}>
                                                                                    {d.daireDurumu}
                                                                                </span>
                                                                            </label>
                                                                        );
                                                                    })}
                                                            </div>
                                                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                                <button style={{ fontSize: 12, padding: '4px 12px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', cursor: 'pointer' }} onClick={() => setEditBelgeAdi(null)}>{t('btn.iptal')}</button>
                                                                <button style={{ fontSize: 12, padding: '4px 12px', border: 'none', borderRadius: 4, background: '#3b82f6', color: '#fff', cursor: 'pointer' }} onClick={handleBelgeGrubuGuncelle} disabled={editGrupSaving}>
                                                                    {editGrupSaving ? t('msg.yukleniyor') : 'Güncelle'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>
        )}

        {/* Proje Özellikleri Modal */}
        {showProjeOzellikModal && (
            <div className="pd-overlay" onClick={() => setShowProjeOzellikModal(false)}>
                <div className="pd-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
                    <div className="pd-modal-head">
                        <h3 style={{ fontSize: 16 }}>📋 Proje Özellikleri</h3>
                        <button className="pd-modal-close" onClick={() => setShowProjeOzellikModal(false)}>×</button>
                    </div>
                    <div className="pd-modal-body">
                        <div style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <label className="pd-label" style={{ margin: 0 }}>Özellikler</label>
                                <button type="button" className="pd-btn pd-btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }}
                                    onClick={() => setProjeOzellikler([...projeOzellikler, { projeTakipNo: proje?.takipNo || '', ozellik: '', deger: '', aktif: true }])}>
                                    + Ekle
                                </button>
                            </div>
                            {projeOzellikler.length === 0 && (
                                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '16px 0', fontSize: 13 }}>
                                    Henüz özellik eklenmemiş. "Ekle" butonuna tıklayarak özellik ekleyebilirsiniz.
                                </div>
                            )}
                            {projeOzellikler.map((oz, idx) => (
                                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                                    <input className="pd-input" style={{ flex: 1 }} placeholder={t('label.ara')} list="pd-proje-ozellik-list"
                                        value={oz.ozellik}
                                        onChange={e => { const n = [...projeOzellikler]; n[idx] = { ...n[idx], ozellik: e.target.value }; setProjeOzellikler(n); }}
                                        onBlur={() => fetchProjeDegerOptions(oz.ozellik)} />
                                    <input className="pd-input" style={{ flex: 1 }} placeholder={t('label.ara')} list={`pd-proje-deger-list-${idx}`}
                                        value={oz.deger}
                                        onChange={e => { const n = [...projeOzellikler]; n[idx] = { ...n[idx], deger: e.target.value }; setProjeOzellikler(n); }} />
                                    <button type="button" style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16, fontWeight: 700, padding: '0 6px' }}
                                        onClick={async () => {
                                            if (oz.projeOzellikId) {
                                                try { await axiosInstance.delete(`/api/ProjeOzellikler/${oz.projeOzellikId}`); } catch {}
                                            }
                                            setProjeOzellikler(projeOzellikler.filter((_, i) => i !== idx));
                                        }}>✕</button>
                                    <datalist id={`pd-proje-deger-list-${idx}`}>
                                        {(projeDegerOptionsMap[oz.ozellik] || []).map(d => <option key={d} value={d} />)}
                                    </datalist>
                                </div>
                            ))}
                            <datalist id="pd-proje-ozellik-list">
                                {projeOzellikOptions.map(o => <option key={o} value={o} />)}
                            </datalist>
                        </div>
                    </div>
                    <div className="pd-modal-foot">
                        <button type="button" className="pd-btn pd-btn-ghost" onClick={() => setShowProjeOzellikModal(false)}>{t('btn.iptal')}</button>
                        <button type="button" className="pd-btn pd-btn-primary" onClick={handleProjeOzellikKaydet} disabled={projeOzellikSaving}>
                            {projeOzellikSaving ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {showImalatKatModal && proje && (
            <div className="pd-overlay" onClick={() => { setShowImalatKatModal(false); setImalatKatForm(null); setImalatKatEditID(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: 640, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px 14px', borderBottom: '1px solid #f1f5f9' }}>
                        <span style={{ fontWeight: 700, fontSize: 17, color: '#1e293b' }}><i className="fas fa-tools" style={{ marginRight: 8, color: '#3b82f6' }}></i>İmalat Kategorileri</span>
                        <button onClick={() => { setShowImalatKatModal(false); setImalatKatForm(null); setImalatKatEditID(null); }} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8' }}>×</button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
                        {imalatKatLoading ? (
                            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{t('msg.yukleniyor')}</div>
                        ) : (
                            <>
                                {imalatKategoriler.length === 0 && !imalatKatForm && (
                                    <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: 14 }}>Henüz kategori eklenmemiş.</div>
                                )}
                                {imalatKategoriler.map(kat => (
                                    <div key={kat.imalatKatID} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 10, border: '1px solid #f1f5f9', marginBottom: 8, background: '#fafafa' }}>
                                        <div>
                                            <span style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{kat.kategoriAdi}</span>
                                            <span style={{ marginLeft: 10, fontSize: 11, background: '#eff6ff', color: '#1d4ed8', borderRadius: 6, padding: '2px 8px' }}>{kat.alanTipi}</span>
                                            {kat.aciklama && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{kat.aciklama}</div>}
                                            {['TekSecim','CokSecim'].includes(kat.alanTipi) && kat.secenekler && (
                                                <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap' as const, gap: 4 }}>
                                                    {JSON.parse(kat.secenekler).map((s: string, i: number) => (
                                                        <span key={i} style={{ fontSize: 11, background: '#e2e8f0', color: '#475569', borderRadius: 6, padding: '1px 7px' }}>{s}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button onClick={() => { setImalatKatEditID(kat.imalatKatID); setImalatKatForm({ kategoriAdi: kat.kategoriAdi, alanTipi: kat.alanTipi, secenekler: kat.secenekler ? JSON.parse(kat.secenekler) : [], aciklama: kat.aciklama || '' }); }} style={{ border: 'none', background: '#eff6ff', color: '#1d4ed8', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 13 }}>{t('btn.duzenle')}</button>
                                            <button onClick={() => handleImalatKatDelete(kat.imalatKatID)} style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 13 }}>{t('btn.sil')}</button>
                                        </div>
                                    </div>
                                ))}

                                {imalatKatForm && (
                                    <div style={{ border: '1px solid #bfdbfe', borderRadius: 12, padding: 18, background: '#f0f7ff', marginTop: 12 }}>
                                        <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b', marginBottom: 12 }}>{imalatKatEditID ? 'Kategoriyi Düzenle' : 'Yeni Kategori'}</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                                            <div>
                                                <label style={{ fontSize: 12, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 4 }}>Kategori Adı *</label>
                                                <input value={imalatKatForm.kategoriAdi} onChange={e => setImalatKatForm({ ...imalatKatForm, kategoriAdi: e.target.value })} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', fontSize: 14, boxSizing: 'border-box' as const }} placeholder={t('label.ara')} />
                                            </div>
                                            <div>
                                                <label style={{ fontSize: 12, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 4 }}>Alan Tipi</label>
                                                <select value={imalatKatForm.alanTipi} onChange={e => setImalatKatForm({ ...imalatKatForm, alanTipi: e.target.value as ImalatKategori['alanTipi'], secenekler: [] })} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', fontSize: 14, boxSizing: 'border-box' as const }}>
                                                    <option value="SerbestMetin">Serbest Metin</option>
                                                    <option value="TekSecim">Tek Seçim</option>
                                                    <option value="CokSecim">Çok Seçim</option>
                                                    <option value="Sayi">Sayı</option>
                                                    <option value="Tarih">Tarih</option>
                                                </select>
                                            </div>
                                        </div>
                                        {['TekSecim','CokSecim'].includes(imalatKatForm.alanTipi) && (
                                            <div style={{ marginBottom: 12 }}>
                                                <label style={{ fontSize: 12, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 4 }}>Seçenekler</label>
                                                <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 6, marginBottom: 8 }}>
                                                    {imalatKatForm.secenekler.map((s, i) => (
                                                        <span key={i} style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: 20, padding: '3px 10px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            {s}
                                                            <button onClick={() => setImalatKatForm({ ...imalatKatForm, secenekler: imalatKatForm.secenekler.filter((_,j)=>j!==i) })} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
                                                        </span>
                                                    ))}
                                                </div>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <input value={imalatSecenekInput} onChange={e => setImalatSecenekInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && imalatSecenekInput.trim()) { setImalatKatForm({ ...imalatKatForm, secenekler: [...imalatKatForm.secenekler, imalatSecenekInput.trim()] }); setImalatSecenekInput(''); e.preventDefault(); }}} style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', fontSize: 14 }} placeholder={t('label.ara')} />
                                                    <button onClick={() => { if (imalatSecenekInput.trim()) { setImalatKatForm({ ...imalatKatForm, secenekler: [...imalatKatForm.secenekler, imalatSecenekInput.trim()] }); setImalatSecenekInput(''); }}} style={{ border: 'none', background: '#3b82f6', color: '#fff', borderRadius: 8, padding: '6px 14px', cursor: 'pointer' }}>{t('btn.ekle')}</button>
                                                </div>
                                            </div>
                                        )}
                                        <div style={{ marginBottom: 14 }}>
                                            <label style={{ fontSize: 12, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 4 }}>{t('label.aciklama')}</label>
                                            <input value={imalatKatForm.aciklama} onChange={e => setImalatKatForm({ ...imalatKatForm, aciklama: e.target.value })} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', fontSize: 14, boxSizing: 'border-box' as const }} placeholder={t('label.aciklama')} />
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                            <button onClick={() => { setImalatKatForm(null); setImalatKatEditID(null); }} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '7px 18px', cursor: 'pointer', fontSize: 14 }}>{t('btn.iptal')}</button>
                                            <button onClick={handleImalatKatSave} style={{ border: 'none', background: '#3b82f6', color: '#fff', borderRadius: 8, padding: '7px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>{t('btn.kaydet')}</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    {!imalatKatForm && (
                        <div style={{ padding: '12px 24px 18px', borderTop: '1px solid #f1f5f9' }}>
                            <button onClick={() => setImalatKatForm({ kategoriAdi: '', alanTipi: 'SerbestMetin', secenekler: [], aciklama: '' })} style={{ border: 'none', background: '#3b82f6', color: '#fff', borderRadius: 10, padding: '9px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <i className="fas fa-plus"></i> Yeni Kategori Ekle
                            </button>
                        </div>
                    )}
                </div>
            </div>
        )}

        {showImalatRaporModal && proje && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowImalatRaporModal(false)}>
                <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: 900, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px 14px', borderBottom: '1px solid #f1f5f9' }}>
                        <span style={{ fontWeight: 700, fontSize: 17, color: '#1e293b' }}><i className="fas fa-chart-bar" style={{ marginRight: 8, color: '#3b82f6' }}></i>İmalat Notları Raporu — {proje.projeAdi}</span>
                        <button onClick={() => setShowImalatRaporModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8' }}>×</button>
                    </div>
                    <div style={{ padding: '12px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 12, flexWrap: 'wrap' as const, alignItems: 'center' }}>
                        <select value={raporFilterKatID} onChange={e => setRaporFilterKatID(e.target.value ? Number(e.target.value) : '')} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', fontSize: 13 }}>
                            <option value="">Tüm Kategoriler</option>
                            {imalatKategoriler.map(k => <option key={k.imalatKatID} value={k.imalatKatID}>{k.kategoriAdi}</option>)}
                        </select>
                        <select value={raporFilterBlokID} onChange={e => setRaporFilterBlokID(e.target.value ? Number(e.target.value) : '')} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', fontSize: 13 }}>
                            <option value="">Tüm Bloklar</option>
                            {bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
                        </select>
                        <button onClick={fetchImalatRapor} style={{ border: 'none', background: '#3b82f6', color: '#fff', borderRadius: 8, padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{t('btn.filtrele')}</button>
                        <button onClick={handleImalatRaporExcel} disabled={!imalatRaporData.length} style={{ border: '1px solid #16a34a', background: '#f0fdf4', color: '#16a34a', borderRadius: 8, padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                            <i className="fas fa-file-excel" style={{ marginRight: 5 }}></i>Excel İndir
                        </button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
                        {imalatRaporLoading ? (
                            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{t('msg.yukleniyor')}</div>
                        ) : imalatRaporData.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 14 }}>{t('msg.veriYok')}</div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc' }}>
                                        {['Daire No','Blok','Kat','Müşteri','Kategori','Değer','Açıklama','Tarih'].map(h => (
                                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' as const }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {imalatRaporData.map((r: any, i: number) => (
                                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                            <td style={{ padding: '7px 12px' }}>{r.daireNo || '—'}</td>
                                            <td style={{ padding: '7px 12px' }}>{r.blokAdi || '—'}</td>
                                            <td style={{ padding: '7px 12px' }}>{r.katAdi || '—'}</td>
                                            <td style={{ padding: '7px 12px' }}>{r.musteriAdSoyad || '—'}</td>
                                            <td style={{ padding: '7px 12px' }}><span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 6, padding: '2px 8px', fontWeight: 500 }}>{r.kategoriAdi || '—'}</span></td>
                                            <td style={{ padding: '7px 12px' }}>{(() => { try { const p = JSON.parse(r.deger || ''); if(Array.isArray(p)) return p.join(', '); } catch {} return r.deger || '—'; })()}</td>
                                            <td style={{ padding: '7px 12px', color: '#64748b' }}>{r.aciklama || ''}</td>
                                            <td style={{ padding: '7px 12px', whiteSpace: 'nowrap' as const, color: '#94a3b8', fontSize: 12 }}>{r.eklenmeTarihi ? new Date(r.eklenmeTarihi).toLocaleDateString('tr-TR') : ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        )}

        {showGorunumModal && proje && (
            <ProjeGorunumleri
                projeID={proje.projeID}
                daireler={daireler}
                bloklar={bloklar.map(b => ({ id: b.blokID, ad: b.blokAdi }))}
                katlar={katlar.map(k => ({ id: k.katID, ad: k.katAdi }))}
                cepheler={cepheler.map(c => ({ id: c.cepheID, ad: c.cepheAdi }))}
                manzaralar={manzaralar.map(m => ({ id: m.manzaraID, ad: m.manzaraAdi }))}
                daireTipleri={daireTipleri.map(dt => ({ id: dt.daireTipID, ad: dt.tip }))}
                onClose={() => setShowGorunumModal(false)}
            />
        )}
        </>
    );
};

export default ProjeDetay;
